import { LanguageCode } from "@/contexts/LanguageContext";

type TranslationKeys = {
  // Common
  search: string;
  signOut: string;
  admin: string;
  user: string;
  save: string;
  cancel: string;
  create: string;
  edit: string;
  delete: string;
  filter: string;
  actions: string;
  noResults: string;
  progress: string;
  findings: string;
  
  // Project Detail
  projectDetails: string;
  projectNotFound: string;
  unlockEdit: string;
  projectSaved: string;
  projectSavedDesc: string;
  documents: string;
  uploadDocuments: string;
  documentsUploaded: string;
  documentsUploadedDesc: string;
  noDocuments: string;
  
  // Navigation
  dashboard: string;
  project: string;
  projects: string;
  tasks: string;
  reports: string;
  usersRoles: string;
  
  // Dashboard
  dashboardTitle: string;
  dashboardSubtitle: string;
  totalProjects: string;
  activeProjects: string;
  totalDealValue: string;
  avgDDProgress: string;
  fromLastMonth: string;
  projectReports: string;
  categoryBreakdown: string;
  
  // Project / Due Diligence
  dueDiligence: string;
  addProject: string;
  projectTarget: string;
  status: string;
  phase: string;
  dealValue: string;
  ddProgress: string;
  risk: string;
  redFlags: string;
  redacted: string;
  showRedacted: string;
  hideRedacted: string;
  redactedCommaSeparated: string;
  expectedCloseDate: string;
  owner: string;
  openProject: string;
  noProjectsFound: string;
  
  // Status
  active: string;
  onHold: string;
  cancelled: string;
  done: string;
  
  // Risk
  low: string;
  medium: string;
  high: string;
  
  // Phases
  initialReview: string;
  finalReview: string;
  closing: string;
  negotiation: string;
  
  // Add Project Form
  createNewProject: string;
  fillDetails: string;
  projectTargetName: string;
  enterProjectName: string;
  selectStatus: string;
  selectPhase: string;
  enterDealValue: string;
  selectRiskLevel: string;
  projectSector: string;
  selectSector: string;
  projectDealType: string;
  selectDealType: string;
  dealTypeAcquisition: string;
  dealTypeMerger: string;
  dealTypeGrowthInvestment: string;
  dealTypePEBuyout: string;
  dealTypeCarveOut: string;
  dealTypeJointVenture: string;
  dealTypeAssetPurchase: string;
  numberOfRedFlags: string;
  selectOwner: string;
  createProject: string;
  projectCreated: string;
  projectCreatedDesc: string;
  projectDeleted: string;
  projectDeletedDesc: string;
  confirmDeleteProject: string;
  confirmDeleteProjectDesc: string;
  deleteHasCreditsError: string;
  projectUpdated: string;
  projectUpdatedDesc: string;
  
  // Users & Roles
  usersRolesTitle: string;
  firstName: string;
  lastName: string;
  email: string;
  role: string;
  department: string;
  raci: string;
  bm: string;
  responsible: string;
  accountable: string;
  consulted: string;
  informed: string;
  allRoles: string;
  allDepartments: string;
  searchUsers: string;
  noUsersFound: string;
  
  // Auth
  welcomeBack: string;
  signInCredentials: string;
  createAccount: string;
  createAccountSubtitle: string;
  signIn: string;
  signUp: string;
  emailAddress: string;
  password: string;
  confirmPassword: string;
  fullName: string;
  rememberMe: string;
  forgotPassword: string;
  noAccount: string;
  haveAccount: string;
  orContinueWith: string;
  agreeTerms: string;
  termsConditions: string;
  signingIn: string;
  creatingAccount: string;
  continueWithGoogle: string;
  continueWithMicrosoft: string;
  continueWithApple: string;
  supabaseRequired: string;
  
  // Welcome
  welcomeToVeritas: string;
  welcomeDescription: string;
  
  // Project Workspace Tabs
  tabOverview: string;
  tabWorkstreams: string;
  tabTasks: string;
  tabDocuments: string;
  tabQA: string;
  tabRisks: string;
  tabApprovals: string;
  projectTimeline: string;
  
  // Workstreams
  workstream: string;
  allOwners: string;
  wsIT: string;
  wsCommercial: string;
  wsOperations: string;
  wsCyber: string;
  wsIP: string;
  wsNotStarted: string;
  wsInProgress: string;
  wsCompleted: string;
  wsBlocked: string;
  // Workstream agent types
  agentCybersecurity: string;
  agentInfrastructureCloud: string;
  agentSoftwareEngineering: string;
  agentDataAI: string;
  agentCompliance: string;
  agentVendorThirdParty: string;
  agentIpLicensing: string;
  agentArchitectureScalability: string;
  agentIncidentOps: string;
  agentPeopleOrg: string;
  agentFinancialDueDiligence: string;
  agentSummarization: string;
  agentContradictionDetector: string;
  agentRedFlagClassifier: string;
  agentGapDetector: string;
  agentCrossDocSynthesis: string;
  // Workstream statuses
  wsPending: string;
  wsRunning: string;
  wsFailed: string;
  // Workstream detail fields
  confidence: string;
  keyGaps: string;
  severity: string;
  documentType: string;
  recommendation: string;
  evidence: string;
  noWorkstreams: string;
  noFindings: string;
  crossAnalysisFindings: string;
  summary: string;
  
  // Tasks
  taskKanban: string;
  addTask: string;
  taskTodo: string;
  taskInProgress: string;
  taskReview: string;
  taskDone: string;
  
  // Documents
  searchDocuments: string;
  allTags: string;
  allStatuses: string;
  documentName: string;
  tag: string;
  version: string;
  reviewStatus: string;
  uploadedBy: string;
  uploadedAt: string;
  docPending: string;
  docInReview: string;
  docApproved: string;
  docRejected: string;
  documentDeleted: string;
  documentDeletedDesc: string;
  
  // Q&A
  totalQuestions: string;
  qaOpen: string;
  qaPending: string;
  qaAnswered: string;
  qaClosed: string;
  allWorkstreams: string;
  addQuestion: string;
  askedOn: string;
  answer: string;
  enterAnswer: string;
  submitAnswer: string;
  
  // Risks
  totalRisks: string;
  riskCritical: string;
  riskHigh: string;
  riskMedium: string;
  riskLow: string;
  riskInfo: string;
  riskHeatmap: string;
  riskRegister: string;
  addRisk: string;
  riskTitle: string;
  score: string;
  likelihood: string;
  impact: string;
  riskIdentified: string;
  riskMitigating: string;
  riskMitigated: string;
  riskAccepted: string;
  
  // Approvals
  approvalGates: string;
  gateDetails: string;
  decidedBy: string;
  conditions: string;
  pending: string;
  go: string;
  noGo: string;
  conditional: string;
  auditTrail: string;
  
  // Activity Log
  activityLog: string;
  justNow: string;
  ago: string;
  
  // Team & Roles Tab
  tabTeamRoles: string;
  tabFolders: string;
  tabPayments: string;

  // Payments
  buyCredits: string;
  creditPackages: string;
  selectPackage: string;
  credits: string;
  paymentHistory: string;
  paymentDate: string;
  paymentStatus: string;
  paymentAmount: string;
  paymentCredits: string;
  paymentPending: string;
  paymentCompleted: string;
  paymentFailed: string;
  paymentRefunded: string;
  paymentCancelled: string;
  noPayments: string;
  checkoutSuccess: string;
  checkoutCancel: string;
  transferCredits: string;
  transferCreditsTitle: string;
  transferCreditsDescription: string;
  transferTargetProject: string;
  transferSelectTarget: string;
  transferAmount: string;
  transferSend: string;
  transferSuccess: string;
  transferError: string;
  transferInsufficientCredits: string;
  transferNoTargets: string;

  // Seats
  seats: string;
  seatsActive: string;
  seatsAvailable: string;
  buySeats: string;
  seatPlans: string;
  selectSeatPlan: string;
  seatPlanBonusCredits: string;
  paymentType: string;
  paymentTypeCredits: string;
  paymentTypeSeats: string;
  seatStatus: string;
  seatActive: string;
  seatInactive: string;
  activateSeat: string;
  deactivateSeat: string;
  noSeatsAvailableTooltip: string;
  seatEntitlementBlockedMessage: string;
  folders: string;
  newFolder: string;
  newSubfolder: string;
  folderName: string;
  createFolder: string;
  renameFolder: string;
  deleteFolder: string;
  deleteFolderConfirm: string;
  folderHasChildren: string;
  noFolders: string;
  uploadFile: string;
  fileTooLarge: string;
  fileTypeNotAllowed: string;
  deleteFile: string;
  deleteFileConfirm: string;
  teamRoles: string;
  totalMembers: string;
  addTeamMember: string;
  addTeamMemberDesc: string;
  importFromDirectory: string;
  importFromDirectoryDesc: string;
  searchTeamMembers: string;
  projectRole: string;
  projectDepartment: string;
  permission: string;
  allPermissions: string;
  allRaci: string;
  projectAdmin: string;
  projectMember: string;
  projectViewer: string;
  memberAdded: string;
  memberAddedDesc: string;
  memberRemoved: string;
  memberRemovedDesc: string;
  membersImported: string;
  membersImportedDesc: string;
  resetToGlobal: string;
  resetToGlobalDesc: string;
  removeMember: string;
  noMembersFound: string;
  noUsersAvailable: string;
  allUsersAdded: string;
  // Project user management
  roleOwner: string;
  roleEditor: string;
  addUserByEmail: string;
  enterUserEmail: string;
  roleChanged: string;
  roleChangedDesc: string;
  confirmRemoveUser: string;
  confirmRemoveUserDesc: string;
  usersSelected: string;
  // Folder user management
  manageFolderUsers: string;
  addFolderMember: string;
  selectProjectUser: string;
  noFolderMembers: string;
  confirmRemoveFolderUser: string;
  confirmRemoveFolderUserDesc: string;
  importUsers: string;
  global: string;
  name: string;
  addToProject: string;
  selectProject: string;
  useGlobalDefaults: string;
  
  // Dashboard Customization
  customize: string;
  customizeDashboard: string;
  customizeDashboardDesc: string;
  enableAll: string;
  disableAll: string;
  resetToDefault: string;
  customizationDone: string;
  
  // Reports & Insights
  reportsSubtitle: string;
  exportBtn: string;
  exportSections: string;
  selectAll: string;
  riskCompliance: string;
  portfolioAnalysis: string;
  complianceAudit: string;
  customReports: string;
  riskHeatmapTitle: string;
  riskDistribution: string;
  riskTrends: string;
  increasingDecreasing: string;
  policyDeviations: string;
  deviationsFromPolicy: string;
  projectLabel: string;
  policyLabel: string;
  deviationLabel: string;
  severityLabel: string;
  lastMonth: string;
  thisMonth: string;
  trend: string;
  it: string;
  critical: string;
  projectVsProject: string;
  compareProjects: string;
  riskScore: string;
  confidenceScore: string;
  benchmarkScore: string;
  riskVsConfidence: string;
  projectPositioning: string;
  vsLastQuarter: string;
  youAreBetter: string;
  checklistCompletion: string;
  completionByCategory: string;
  decisionHistory: string;
  whoApprovedWhat: string;
  fullAuditTrail: string;
  completeTraceability: string;
  timestamp: string;
  userLabel: string;
  actionLabel: string;
  objectLabel: string;
  approved: string;
  rejected: string;
  submitted: string;
  updated: string;
  savedTemplates: string;
  yourTemplates: string;
  lastUsed: string;
  newTemplate: string;
  buildOwnReport: string;
  selectKpis: string;
  selectChartType: string;
  barChart: string;
  lineChart: string;
  pieChart: string;
  heatmapChart: string;
  dataSources: string;
  allProjects: string;
  activeProjectsFilter: string;
  completedProjects: string;
  last30Days: string;
  lastQuarter: string;
  lastYear: string;
  preview: string;
  saveAsTemplate: string;
  completionRate: string;
  riskCount: string;
  approvals: string;
  review: string;

  // Settings
  settings: string;
  settingsTitle: string;
  settingsSubtitle: string;
  profileSettings: string;
  displayName: string;
  displayNamePlaceholder: string;
  themeSettings: string;
  themeLight: string;
  themeDark: string;
  themeSystem: string;
  languageSettings: string;
  settingsSaved: string;
  settingsSavedDesc: string;
  settingsError: string;

  // Organizations
  organization: string;
  organizations: string;
  organizationsSubtitle: string;
  addOrganization: string;
  createNewOrganization: string;
  fillOrganizationDetails: string;
  organizationName: string;
  description: string;
  organizationNumber: string;
  contactEmail: string;
  country: string;
  organizationOwner: string;
  createOrganization: string;
  organizationNotFound: string;
  organizationCreditBalance: string;
  organizationProjects: string;
  organizationUsers: string;
  organizationDetails: string;
  lastUpdated: string;
  createdAt: string;
  viewProjects: string;
  searchOrganizations: string;
  openOrganization: string;
  noOrganizationsFound: string;
  confirmDeleteOrganization: string;
  confirmDeleteOrganizationDesc: string;
  organizationDeleteBlocked: string;
  selectOrganization: string;
  createOrganizationFirst: string;
  addOrganizationMemberDesc: string;
  inviteMember: string;
  inviteMemberDesc: string;
  pendingInvitationBadge: string;
  pendingInvitationsTitle: string;
  acceptInvitation: string;
  declineInvitation: string;
  invitedToJoin: string;
  organizationMembersLoadError: string;
  confirmRemoveOrganizationUserDesc: string;
  addExistingProject: string;
  moveProjectWarning: string;
  moveProject: string;
  searchProjectsPlaceholder: string;
  noMatchingProjects: string;
  searchToFindProjects: string;
  transferTargetOrganization: string;
  previous: string;
  next: string;

  // Report Generation
  generateReport: string;
  generatingReport: string;
  reportGenerated: string;
  confirmGenerateReport: string;
  confirmGenerateReportDesc: string;
  projectCreditBalance: string;
  creditsUsableUntil: string;
  creditsUsableUntilUnavailable: string;
  requiredCredits: string;
  insufficientProjectCredits: string;

  // Technical Documentation Guide
  technicalDocumentation: string;
  techDocTitle: string;
  techDocSubtitle: string;
  techDocIntro: string;
  techDocRequired: string;
  techDocRecommended: string;
  techDocS1Title: string; techDocS1Desc: string; techDocS1I1: string; techDocS1I2: string; techDocS1I3: string; techDocS1I4: string;
  techDocS2Title: string; techDocS2Desc: string; techDocS2I1: string; techDocS2I2: string; techDocS2I3: string; techDocS2I4: string;
  techDocS3Title: string; techDocS3Desc: string; techDocS3I1: string; techDocS3I2: string; techDocS3I3: string; techDocS3I4: string;
  techDocS4Title: string; techDocS4Desc: string; techDocS4I1: string; techDocS4I2: string; techDocS4I3: string; techDocS4I4: string;
  techDocS5Title: string; techDocS5Desc: string; techDocS5I1: string; techDocS5I2: string; techDocS5I3: string; techDocS5I4: string;
  techDocS6Title: string; techDocS6Desc: string; techDocS6I1: string; techDocS6I2: string; techDocS6I3: string; techDocS6I4: string;
  techDocS7Title: string; techDocS7Desc: string; techDocS7I1: string; techDocS7I2: string; techDocS7I3: string; techDocS7I4: string;
  techDocS8Title: string; techDocS8Desc: string; techDocS8I1: string; techDocS8I2: string; techDocS8I3: string; techDocS8I4: string;
  techDocS9Title: string; techDocS9Desc: string; techDocS9I1: string; techDocS9I2: string; techDocS9I3: string; techDocS9I4: string;
  techDocS10Title: string; techDocS10Desc: string; techDocS10I1: string; techDocS10I2: string; techDocS10I3: string; techDocS10I4: string;
  techDocS11Title: string; techDocS11Desc: string; techDocS11I1: string; techDocS11I2: string; techDocS11I3: string; techDocS11I4: string;
  techDocS12Title: string; techDocS12Desc: string; techDocS12I1: string; techDocS12I2: string; techDocS12I3: string; techDocS12I4: string;
  techDocS13Title: string; techDocS13Desc: string; techDocS13I1: string; techDocS13I2: string; techDocS13I3: string; techDocS13I4: string;
  techDocS14Title: string; techDocS14Desc: string; techDocS14I1: string; techDocS14I2: string; techDocS14I3: string; techDocS14I4: string;
};

const translations: Record<LanguageCode, TranslationKeys> = {
  en: {
    // Common
    search: "Search...",
    signOut: "Sign Out",
    admin: "Admin",
    user: "User",
    save: "Save",
    cancel: "Cancel",
    create: "Create",
    edit: "Edit",
    delete: "Delete",
    filter: "Filter",
    actions: "Actions",
    noResults: "No results found",
    
    // Project Detail
    projectDetails: "Project Details",
    projectNotFound: "Project not found",
    unlockEdit: "Unlock & Edit",
    projectSaved: "Project Saved",
    projectSavedDesc: "The project has been successfully updated.",
    documents: "Documents",
    uploadDocuments: "Upload Documents",
    documentsUploaded: "Documents Uploaded",
    documentsUploadedDesc: "documents have been uploaded.",
    noDocuments: "No documents uploaded yet.",
    
    // Navigation
    dashboard: "Dashboard",
    project: "Project",
    projects: "Projects",
    tasks: "Task/Actions",
    reports: "Reports & Insights",
    usersRoles: "Users & Roles",
    
    // Dashboard
    dashboardTitle: "Dashboard Overview",
    dashboardSubtitle: "Track your due diligence pipeline and progress",
    totalProjects: "Total Projects",
    activeProjects: "Active Projects",
    totalDealValue: "Total Project Value",
    avgDDProgress: "Avg DD Progress",
    fromLastMonth: "from last month",
    projectReports: "Project Reports",
    categoryBreakdown: "Category Breakdown",
    
    // Project / Due Diligence
    dueDiligence: "Due Diligence",
    addProject: "Add Project",
    projectTarget: "Target Name",
    status: "Status",
    phase: "Phase",
    dealValue: "Project Value",
    ddProgress: "Progress",
    risk: "Risk",
    redFlags: "Red Flags",
    redacted: "Redacted Content",
    showRedacted: "Show redacted content",
    hideRedacted: "Hide redacted content",
    redactedCommaSeparated: "Separate multiple keywords with commas",
    expectedCloseDate: "Expected Close Date",
    owner: "Owner",
    openProject: "Open Projects",
    noProjectsFound: "No projects found matching your filters.",
    
    // Status
    active: "Active",
    onHold: "On Hold",
    cancelled: "Cancelled",
    done: "Done",
    
    // Risk
    low: "Low",
    medium: "Medium",
    high: "High",
    
    // Phases
    initialReview: "Initial Review",
    finalReview: "Final Review",
    negotiation: "Negotiation",
    closing: "Sign-off",
    
    // Add Project Form
    createNewProject: "Create New Project",
    fillDetails: "Fill in the details below to create a new due diligence project.",
    projectTargetName: "Name",
    enterProjectName: "Name",
    selectStatus: "Select status",
    selectPhase: "Select phase",
    enterDealValue: "Enter acquisition value",
    selectRiskLevel: "Select risk level",
    projectSector: "Sector",
    selectSector: "Select sector",
    projectDealType: "Deal Type",
    selectDealType: "Select deal type",
    dealTypeAcquisition: "Acquisition",
    dealTypeMerger: "Merger",
    dealTypeGrowthInvestment: "Growth Investment",
    dealTypePEBuyout: "PE Buyout",
    dealTypeCarveOut: "Carve-Out",
    dealTypeJointVenture: "Joint Venture",
    dealTypeAssetPurchase: "Asset Purchase",
    numberOfRedFlags: "Number of redacted keywords",
    selectOwner: "Select owner",
    createProject: "Create Project",
    projectCreated: "Project Created",
    projectCreatedDesc: "The project has been successfully created.",
    projectDeleted: "Project Deleted",
    projectDeletedDesc: "The project has been successfully deleted.",
    confirmDeleteProject: "Delete Project",
    confirmDeleteProjectDesc: "Are you sure you want to permanently delete",
    deleteHasCreditsError: "Cannot delete a project that has a positive credit balance. Transfer or spend the credits first.",
    projectUpdated: "Project Updated",
    projectUpdatedDesc: "The project has been successfully updated.",
    
    // Users & Roles
    usersRolesTitle: "Users & Roles Management",
    firstName: "First Name",
    lastName: "Last Name",
    email: "Email",
    role: "Role",
    department: "Department",
    raci: "RACI",
    bm: "BM",
    responsible: "Responsible",
    accountable: "Accountable",
    consulted: "Consulted",
    informed: "Informed",
    allRoles: "All Roles",
    allDepartments: "All Departments",
    searchUsers: "Search users...",
    noUsersFound: "No users found matching your criteria.",
    
    // Auth
    welcomeBack: "Welcome Back!",
    signInCredentials: "Please enter your credentials to sign in",
    createAccount: "Create Account",
    createAccountSubtitle: "Create your account to get started",
    signIn: "Sign In",
    signUp: "Sign Up",
    emailAddress: "Email Address",
    password: "Password",
    confirmPassword: "Confirm Password",
    fullName: "Full Name",
    rememberMe: "Remember me",
    forgotPassword: "Forgot password?",
    noAccount: "Don't have an account?",
    haveAccount: "Already have an account?",
    orContinueWith: "Or",
    agreeTerms: "I agree to the",
    termsConditions: "Terms and Conditions",
    signingIn: "Signing in...",
    creatingAccount: "Creating account...",
    continueWithGoogle: "Continue with Google",
    continueWithMicrosoft: "Continue with Microsoft",
    continueWithApple: "Continue with Apple",
    supabaseRequired: "Please connect Supabase to enable OAuth login",
    
    // Welcome
    welcomeToVeritas: "Welcome to Perseon Labs - Veritas",
    welcomeDescription: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    
    // New keys
    progress: "Progress",
    findings: "Findings",
    tabOverview: "Overview",
    tabWorkstreams: "Workstreams",
    tabTasks: "Tasks",
    tabDocuments: "Documents",
    tabQA: "Q&A",
    tabRisks: "Risks/Findings",
    tabApprovals: "Approvals",
    projectTimeline: "Project Timeline",
    workstream: "Workstream",
    allOwners: "All Owners",
    wsIT: "IT",
    wsCommercial: "Commercial",
    wsOperations: "Operations",
    wsCyber: "Cyber Security",
    wsIP: "IP",
    wsNotStarted: "Not Started",
    wsInProgress: "In Progress",
    wsCompleted: "Completed",
    wsBlocked: "Blocked",
    agentCybersecurity: "Cybersecurity",
    agentInfrastructureCloud: "Infrastructure & Cloud",
    agentSoftwareEngineering: "Software Engineering",
    agentDataAI: "Data & AI",
    agentCompliance: "Compliance",
    agentVendorThirdParty: "Vendor & Third Party",
    agentIpLicensing: "IP & Licensing",
    agentArchitectureScalability: "Architecture & Scalability",
    agentIncidentOps: "Incident & Operations",
    agentPeopleOrg: "People & Organization",
    agentFinancialDueDiligence: "Financial Due Diligence",
    agentSummarization: "Summarization",
    agentContradictionDetector: "Contradiction Detector",
    agentRedFlagClassifier: "Red Flag Classifier",
    agentGapDetector: "Gap Detector",
    agentCrossDocSynthesis: "Cross-Document Synthesis",
    wsPending: "Pending",
    wsRunning: "Running",
    wsFailed: "Failed",
    riskScore: "Risk Score",
    confidence: "Confidence",
    keyGaps: "Key Gaps",
    severity: "Severity",
    documentType: "Document Type",
    recommendation: "Recommendation",
    evidence: "Evidence",
    noWorkstreams: "No workstreams found for this project",
    noFindings: "No findings available",
    crossAnalysisFindings: "Cross-Analysis Results",
    summary: "Summary",
    taskKanban: "Task Board",
    addTask: "Add Task",
    taskTodo: "To Do",
    taskInProgress: "In Progress",
    taskReview: "Review",
    taskDone: "Done",
    searchDocuments: "Search documents...",
    allTags: "All Tags",
    allStatuses: "All Statuses",
    documentName: "Document Name",
    tag: "Tag",
    version: "Version",
    reviewStatus: "Review Status",
    uploadedBy: "Uploaded By",
    uploadedAt: "Uploaded At",
    docPending: "Pending",
    docInReview: "In Review",
    docApproved: "Approved",
    docRejected: "Rejected",
    documentDeleted: "Document Deleted",
    documentDeletedDesc: "The document has been deleted.",
    totalQuestions: "Total Questions",
    qaOpen: "Open",
    qaPending: "Pending",
    qaAnswered: "Answered",
    qaClosed: "Closed",
    allWorkstreams: "All Workstreams",
    addQuestion: "Add Question",
    askedOn: "Asked on",
    answer: "Answer",
    enterAnswer: "Enter your answer...",
    submitAnswer: "Submit Answer",
    totalRisks: "Total Risks",
    riskCritical: "Critical",
    riskHigh: "High Risk",
    riskMedium: "Medium Risk",
    riskLow: "Low Risk",
    riskInfo: "Info",
    riskHeatmap: "Risk Heatmap",
    riskRegister: "Risk Register",
    addRisk: "Add Risk",
    riskTitle: "Risk",
    score: "Score",
    likelihood: "Likelihood",
    impact: "Impact",
    riskIdentified: "Identified",
    riskMitigating: "Mitigating",
    riskMitigated: "Mitigated",
    riskAccepted: "Accepted",
    approvalGates: "Approval Gates",
    gateDetails: "Gate Details",
    decidedBy: "Decided by",
    conditions: "Conditions",
    pending: "Pending",
    go: "Go",
    noGo: "No-Go",
    conditional: "Conditional",
    auditTrail: "Audit Trail",
    activityLog: "Activity Log",
    justNow: "Just now",
    ago: "ago",
    
    // Team & Roles Tab
    tabTeamRoles: "Team & Roles",
    tabFolders: "Folders",
    tabPayments: "Transactions",

    // Payments
    buyCredits: "Buy Credits",
    creditPackages: "Credit Packages",
    selectPackage: "Select a Package",
    credits: "credits",
    paymentHistory: "Transaction History",
    paymentDate: "Date",
    paymentStatus: "Status",
    paymentAmount: "Amount",
    paymentCredits: "Credits",
    paymentPending: "Pending",
    paymentCompleted: "Completed",
    paymentFailed: "Failed",
    paymentRefunded: "Refunded",
    paymentCancelled: "Cancelled",
    noPayments: "No transactions yet",
    checkoutSuccess: "Payment successful! Credits have been added to your project.",
    checkoutCancel: "Payment was cancelled.",
    transferCredits: "Transfer Credits",
    transferCreditsTitle: "Transfer Credits",
    transferCreditsDescription: "Move credits from this organization to another organization you own.",
    transferTargetProject: "Target organization",
    transferSelectTarget: "Select an organization",
    transferAmount: "Amount",
    transferSend: "Send",
    transferSuccess: "Credits transferred successfully.",
    transferError: "Failed to transfer credits.",
    transferInsufficientCredits: "Not enough credits available.",
    transferNoTargets: "You don't own any other organizations to transfer credits to.",

    // Seats
    seats: "Seats",
    seatsActive: "Active",
    seatsAvailable: "Available",
    buySeats: "Buy Seats",
    seatPlans: "Seat Plans",
    selectSeatPlan: "Select a Plan",
    seatPlanBonusCredits: "credits bonus",
    paymentType: "Type",
    paymentTypeCredits: "Credits",
    paymentTypeSeats: "Seats",
    seatStatus: "Seat",
    seatActive: "Active",
    seatInactive: "Inactive",
    activateSeat: "Activate seat",
    deactivateSeat: "Deactivate seat",
    noSeatsAvailableTooltip: "No seats available in the pool",
    seatEntitlementBlockedMessage: "Your organization needs more active seats to do this. Ask an owner or editor to buy or activate seats.",
    folders: "Folders",
    newFolder: "New Folder",
    newSubfolder: "New Subfolder",
    folderName: "Folder name",
    createFolder: "Create folder",
    renameFolder: "Rename folder",
    deleteFolder: "Delete folder",
    deleteFolderConfirm: "Are you sure you want to delete this folder? This action cannot be undone.",
    folderHasChildren: "Cannot delete a folder that contains subfolders. Delete the subfolders first.",
    noFolders: "No folders yet. Create one to get started.",
    uploadFile: "Upload file",
    fileTooLarge: "File exceeds the 50 MB limit.",
    fileTypeNotAllowed: "File type not allowed. Only documents, plain text, and source code files are permitted.",
    deleteFile: "Delete file",
    deleteFileConfirm: "Are you sure you want to delete this file? This action cannot be undone.",
    teamRoles: "Team & Roles",
    totalMembers: "Total Members",
    addTeamMember: "Add Team Member",
    addTeamMemberDesc: "Search and add a user to this project team.",
    importFromDirectory: "Import from Directory",
    importFromDirectoryDesc: "Select multiple users from the global directory to add to this project.",
    searchTeamMembers: "Search team members...",
    projectRole: "Project Role",
    projectDepartment: "Project Dept",
    permission: "Permission",
    allPermissions: "All Permissions",
    allRaci: "All RACI",
    projectAdmin: "Project Admin",
    projectMember: "Member",
    projectViewer: "Viewer",
    memberAdded: "Member Added",
    memberAddedDesc: "The user has been added to the project team.",
    memberRemoved: "Member Removed",
    memberRemovedDesc: "The user has been removed from the project team.",
    membersImported: "Members Imported",
    membersImportedDesc: "users have been added to the project team.",
    resetToGlobal: "Reset to Global",
    resetToGlobalDesc: "Project overrides have been reset to global defaults.",
    removeMember: "Remove Member",
    noMembersFound: "No team members found matching your criteria.",
    noUsersAvailable: "No users available to add.",
    allUsersAdded: "All users have been added to this project.",
    roleOwner: "Owner",
    roleEditor: "Editor",
    addUserByEmail: "Add User by Email",
    enterUserEmail: "Enter user email address...",
    roleChanged: "Role Updated",
    roleChangedDesc: "The user's role has been changed.",
    confirmRemoveUser: "Remove user from project?",
    confirmRemoveUserDesc: "This will revoke the user's access to the project. This action cannot be undone.",
    usersSelected: "users selected",
    manageFolderUsers: "Manage folder users",
    addFolderMember: "Add member",
    selectProjectUser: "Select a project member...",
    noFolderMembers: "No users have been assigned to this folder.",
    confirmRemoveFolderUser: "Remove user from folder?",
    confirmRemoveFolderUserDesc: "This will revoke the user's folder-level access. This action cannot be undone.",
    importUsers: "Import Selected",
    global: "Global",
    name: "Name",
    addToProject: "Add to Project",
    selectProject: "Select Project",
    useGlobalDefaults: "Use global defaults",
    
    // Dashboard Customization
    customize: "Customize",
    customizeDashboard: "Customize Dashboard",
    customizeDashboardDesc: "Choose which widgets to display on your dashboard",
    enableAll: "Enable All",
    disableAll: "Disable All",
    resetToDefault: "Reset to Default",
    customizationDone: "Done",
    
    // Reports & Insights
    reportsSubtitle: "Reports, insights and analyses",
    exportBtn: "Export",
    exportSections: "Sections to export",
    selectAll: "Select all",
    riskCompliance: "Risk & Compliance",
    portfolioAnalysis: "Portfolio Analysis",
    complianceAudit: "Compliance & Audit",
    customReports: "Custom Reports",
    riskHeatmapTitle: "Risk Heatmap",
    riskDistribution: "Risk distribution by category",
    riskTrends: "Risk Trends",
    increasingDecreasing: "Increasing/decreasing risk over time",
    policyDeviations: "Policy Deviations",
    deviationsFromPolicy: "Deviations from policy and thresholds",
    projectLabel: "Project",
    policyLabel: "Policy",
    deviationLabel: "Deviation",
    severityLabel: "Severity",
    lastMonth: "Last month",
    thisMonth: "This month",
    trend: "Trend",
    it: "IT",
    critical: "Critical",
    projectVsProject: "Project vs Project",
    compareProjects: "Compare projects based on risk and confidence",
    confidenceScore: "Confidence Score",
    benchmarkScore: "Benchmark Score",
    riskVsConfidence: "Risk vs Confidence Matrix",
    projectPositioning: "Project positioning",
    vsLastQuarter: "vs last quarter",
    youAreBetter: "You are better",
    checklistCompletion: "Checklist Completion Rate",
    completionByCategory: "Completion of checklists by category",
    decisionHistory: "Decision History",
    whoApprovedWhat: "Who approved what – when",
    fullAuditTrail: "Full Audit Trail",
    completeTraceability: "Complete traceability for all activities",
    timestamp: "Timestamp",
    userLabel: "User",
    actionLabel: "Action",
    objectLabel: "Object",
    approved: "Approved",
    rejected: "Rejected",
    submitted: "Submitted",
    updated: "Updated",
    savedTemplates: "Saved Templates",
    yourTemplates: "Your report templates",
    lastUsed: "Last used",
    newTemplate: "New Template",
    buildOwnReport: "Build Your Own Report",
    selectKpis: "Select KPIs",
    selectChartType: "Select Chart Type",
    barChart: "Bar",
    lineChart: "Line",
    pieChart: "Pie",
    heatmapChart: "Heatmap",
    dataSources: "Data Sources",
    allProjects: "All projects",
    activeProjectsFilter: "Active projects",
    completedProjects: "Completed projects",
    last30Days: "Last 30 days",
    lastQuarter: "Last quarter",
    lastYear: "Last year",
    preview: "Preview",
    saveAsTemplate: "Save as Template",
    completionRate: "Completion Rate",
    riskCount: "Risk Count",
    approvals: "Approvals",
    review: "Review",

    // Settings
    settings: "Settings",
    settingsTitle: "User Settings",
    settingsSubtitle: "Manage your profile, appearance and language preferences",
    profileSettings: "Profile",
    displayName: "Display Name",
    displayNamePlaceholder: "Enter your display name",
    themeSettings: "Appearance",
    themeLight: "Light",
    themeDark: "Dark",
    themeSystem: "System",
    languageSettings: "Language",
    settingsSaved: "Settings Saved",
    settingsSavedDesc: "Your preferences have been updated.",
    settingsError: "Failed to save settings. Please try again.",

    // Organizations
    organization: "Organization",
    organizations: "Organizations",
    organizationsSubtitle: "Manage organizations, members, projects, and credits.",
    addOrganization: "Add Organization",
    createNewOrganization: "Create New Organization",
    fillOrganizationDetails: "Fill in organization details.",
    organizationName: "Organization Name",
    description: "Description",
    organizationNumber: "Organization Number",
    contactEmail: "Contact Email",
    country: "Country",
    organizationOwner: "Organization Owner",
    createOrganization: "Create Organization",
    organizationNotFound: "Organization not found",
    organizationCreditBalance: "Organization Credit Balance",
    organizationProjects: "Projects",
    organizationUsers: "Users",
    organizationDetails: "Organization Details",
    lastUpdated: "Last updated",
    createdAt: "Created",
    viewProjects: "View projects",
    searchOrganizations: "Search organizations...",
    openOrganization: "Open organization",
    noOrganizationsFound: "No organizations found",
    confirmDeleteOrganization: "Delete organization?",
    confirmDeleteOrganizationDesc: "This permanently deletes",
    organizationDeleteBlocked: "Organizations can only be deleted when credit balance is 0 and no projects belong to them.",
    selectOrganization: "Select organization",
    createOrganizationFirst: "Create an organization before adding a project.",
    addOrganizationMemberDesc: "Add an organization member by email.",
    inviteMember: "Invite",
    inviteMemberDesc: "Invite a user to this organization by email. They must accept the invitation before they can access the organization's data.",
    pendingInvitationBadge: "Pending",
    pendingInvitationsTitle: "Pending Invitations",
    acceptInvitation: "Accept",
    declineInvitation: "Decline",
    invitedToJoin: "invited you to join",
    organizationMembersLoadError: "Failed to load organization members.",
    confirmRemoveOrganizationUserDesc: "Remove this user from the organization?",
    addExistingProject: "Add Existing Project",
    moveProjectWarning: "Moving a project changes which organization owns its credits and permissions context.",
    moveProject: "Move Project",
    searchProjectsPlaceholder: "Search projects by name...",
    noMatchingProjects: "No matching projects found.",
    searchToFindProjects: "Search to find a project you can add to this organization.",
    transferTargetOrganization: "Target Organization",
    previous: "Previous",
    next: "Next",

    // Report Generation
    generateReport: "Run agents",
    generatingReport: "Running...",
    reportGenerated: "AI Workflows Complete",
    confirmGenerateReport: "Confirm running agents",
    confirmGenerateReportDesc: "Are you sure you want to run AI workflows for this project? This will process all uploaded files.",
    projectCreditBalance: "Credit Balance",
    creditsUsableUntil: "Credits usable until",
    creditsUsableUntilUnavailable: "Credits usable-until date unavailable",
    requiredCredits: "Minimum Required Credits",
    insufficientProjectCredits: "Insufficient credits for this run.",

    // Technical Documentation Guide
    technicalDocumentation: "Guide",
    techDocTitle: "Due Diligence Guide",
    techDocSubtitle: "A comprehensive checklist of documentation a buyer should require from a seller during due diligence.",
    techDocIntro: "Use this guide to ensure you have received and reviewed all critical documents from the target company before completing your acquisition.",
    techDocRequired: "Required",
    techDocRecommended: "Recommended",
    techDocS1Title: "System Architecture",
    techDocS1Desc: "Documentation that describes how the system is structured, how components interact, and what technologies are used.",
    techDocS1I1: "High-level architecture diagrams (monolith, microservices, serverless)",
    techDocS1I2: "Component and service interaction diagrams",
    techDocS1I3: "Technology stack documentation (languages, frameworks, databases)",
    techDocS1I4: "Data flow and integration diagrams",
    techDocS2Title: "Source Code & Repository Management",
    techDocS2Desc: "Evidence of how source code is managed, reviewed, and maintained.",
    techDocS2I1: "Access to source code repositories (GitHub, GitLab, Bitbucket)",
    techDocS2I2: "Branching strategy and release process documentation",
    techDocS2I3: "Code review policies and quality standards",
    techDocS2I4: "Third-party library and dependency inventory with licenses",
    techDocS3Title: "Infrastructure & Cloud",
    techDocS3Desc: "Documentation covering hosting environments, cloud setup, and network architecture.",
    techDocS3I1: "Hosting and cloud infrastructure documentation (AWS, Azure, GCP)",
    techDocS3I2: "Infrastructure-as-Code scripts (Terraform, CloudFormation, Pulumi)",
    techDocS3I3: "Network topology and architecture diagrams",
    techDocS3I4: "Scalability architecture and load balancing configuration",
    techDocS4Title: "CI/CD & DevOps",
    techDocS4Desc: "Processes and tooling used to build, test, and deploy software reliably.",
    techDocS4I1: "CI/CD pipeline documentation and tooling (Jenkins, GitHub Actions, etc.)",
    techDocS4I2: "Deployment procedures and rollback plans",
    techDocS4I3: "Environment configuration (development, staging, production)",
    techDocS4I4: "Release cadence, versioning policy, and changelog history",
    techDocS5Title: "Database & Data Management",
    techDocS5Desc: "Documentation proving data is well-structured, backed up, and recoverable.",
    techDocS5I1: "Database schema and entity-relationship (ER) diagrams",
    techDocS5I2: "Data retention and archival policies",
    techDocS5I3: "Backup and recovery procedures with test results",
    techDocS5I4: "Data migration history and tooling documentation",
    techDocS6Title: "Security & Access Control",
    techDocS6Desc: "Policies and controls that protect the system from unauthorized access and attacks.",
    techDocS6I1: "Identity and access management (IAM) documentation",
    techDocS6I2: "Secrets management approach (vaults, key rotation policies)",
    techDocS6I3: "Network security documentation (firewalls, VPNs, segmentation)",
    techDocS6I4: "Security policies and procedures documentation",
    techDocS7Title: "Compliance & Certifications",
    techDocS7Desc: "Formal certifications and audit reports demonstrating regulatory and security compliance.",
    techDocS7I1: "Security certifications (ISO 27001, SOC 2 Type II, PCI-DSS)",
    techDocS7I2: "Privacy compliance documentation (GDPR, CCPA, PIPEDA)",
    techDocS7I3: "Penetration test reports from the last 12–24 months",
    techDocS7I4: "Vulnerability management history and patch cadence",
    techDocS8Title: "API & Third-Party Integrations",
    techDocS8Desc: "Documentation of how the system exposes data and connects with external services.",
    techDocS8I1: "API specifications and documentation (OpenAPI/Swagger)",
    techDocS8I2: "Third-party integrations inventory with vendor SLAs",
    techDocS8I3: "Authentication and authorization documentation (OAuth, SSO, API keys)",
    techDocS8I4: "Webhook and event-driven integration documentation",
    techDocS9Title: "Disaster Recovery & Business Continuity",
    techDocS9Desc: "Plans and evidence that the system can recover from failures and continue operating.",
    techDocS9I1: "Disaster recovery (DR) plan and business continuity plan (BCP)",
    techDocS9I2: "RTO/RPO targets and historical test results",
    techDocS9I3: "Incident response procedures and escalation documentation",
    techDocS9I4: "Post-incident reports from the last 24 months",
    techDocS10Title: "Technical Debt & Roadmap",
    techDocS10Desc: "Transparency on known limitations, future plans, and open-source obligations.",
    techDocS10I1: "Known technical debt inventory and prioritization",
    techDocS10I2: "End-of-life or deprecated component and library list",
    techDocS10I3: "Product and technical roadmap (12–24 months)",
    techDocS10I4: "Open-source license obligations and compliance",
    techDocS11Title: "Financial Statements & Audit",
    techDocS11Desc: "Audited financial statements and independent review reports covering the past three to five fiscal years.",
    techDocS11I1: "Audited annual financial statements (P&L, balance sheet, cash flow) for the last 3–5 years",
    techDocS11I2: "Independent auditor reports and management letters",
    techDocS11I3: "Interim financial statements and management accounts (current year)",
    techDocS11I4: "Accounting policies and any restatements or prior-year adjustments",
    techDocS12Title: "Revenue & Business Model",
    techDocS12Desc: "Detailed breakdown of revenue streams, customer concentration, and the sustainability of the business model.",
    techDocS12I1: "Revenue breakdown by product, service line, geography, and customer segment",
    techDocS12I2: "Top-10 customer list with contract terms, renewal dates, and churn data",
    techDocS12I3: "Recurring vs. one-time revenue analysis and cohort retention data",
    techDocS12I4: "Pricing model documentation and historical pricing changes",
    techDocS13Title: "Working Capital & Cash Flow",
    techDocS13Desc: "Analysis of the company's liquidity, cash generation, and existing debt obligations.",
    techDocS13I1: "Working capital analysis (AR/AP aging, inventory levels, days outstanding)",
    techDocS13I2: "Historical and projected cash flow statements with key assumptions",
    techDocS13I3: "Debt schedule, credit facilities, covenants, and any pledged assets",
    techDocS13I4: "Capital expenditure history and maintenance vs. growth CapEx split",
    techDocS14Title: "Tax & Legal Obligations",
    techDocS14Desc: "Overview of tax compliance, outstanding liabilities, and any material legal or regulatory exposures.",
    techDocS14I1: "Filed tax returns for the last 3–5 years (corporate income tax, VAT/GST)",
    techDocS14I2: "Outstanding tax disputes, assessments, or open audit queries",
    techDocS14I3: "Schedule of contingent liabilities, provisions, and off-balance-sheet items",
    techDocS14I4: "Material contracts, pending litigation, and regulatory correspondence",
  },
  
  sv: {
    // Common
    search: "Sök...",
    signOut: "Logga ut",
    admin: "Admin",
    user: "Användare",
    save: "Spara",
    cancel: "Avbryt",
    create: "Skapa",
    edit: "Redigera",
    delete: "Radera",
    filter: "Filtrera",
    actions: "Åtgärder",
    noResults: "Inga resultat hittades",
    
    // Project Detail
    projectDetails: "Projektdetaljer",
    projectNotFound: "Projektet hittades inte",
    unlockEdit: "Lås upp & Redigera",
    projectSaved: "Projekt Sparat",
    projectSavedDesc: "Projektet har uppdaterats.",
    documents: "Dokument",
    uploadDocuments: "Ladda upp Dokument",
    documentsUploaded: "Dokument Uppladdade",
    documentsUploadedDesc: "dokument har laddats upp.",
    noDocuments: "Inga dokument har laddats upp ännu.",
    
    // Navigation
    dashboard: "Översikt",
    project: "Projekt",
    projects: "Projekt",
    tasks: "Uppgifter/Åtgärder",
    reports: "Rapporter & Insikter",
    usersRoles: "Användare & Roller",
    
    // Dashboard
    dashboardTitle: "Översikt",
    dashboardSubtitle: "Följ din due diligence-pipeline och framsteg",
    totalProjects: "Totala Projekt",
    activeProjects: "Aktiva Projekt",
    totalDealValue: "Totalt Projektvärde",
    avgDDProgress: "Genomsnittlig DD-framsteg",
    fromLastMonth: "från förra månaden",
    projectReports: "Projektrapporter",
    categoryBreakdown: "Kategorifördelning",
    
    // Project / Due Diligence
    dueDiligence: "Due Diligence",
    addProject: "Lägg till Projekt",
    projectTarget: "Målnamn",
    status: "Status",
    phase: "Fas",
    dealValue: "Projektvärde",
    ddProgress: "Framsteg",
    risk: "Risk",
    redFlags: "Röda flaggor",
    redacted: "Borttaget innehåll",
    showRedacted: "Visa redigerat innehåll",
    hideRedacted: "Dölj redigerat innehåll",
    redactedCommaSeparated: "Separera flera nyckelord med kommatecken",
    expectedCloseDate: "Förväntat stängningsdatum",
    owner: "Ägare",
    openProject: "Öppna Projekt",
    noProjectsFound: "Inga projekt hittades som matchar dina filter.",
    
    // Status
    active: "Aktiv",
    onHold: "Pausad",
    cancelled: "Avbruten",
    done: "Klar",
    
    // Risk
    low: "Låg",
    medium: "Medel",
    high: "Hög",
    
    // Phases
    initialReview: "Inledande Granskning",
    finalReview: "Slutgranskning",
    negotiation: "Förhandling",
    closing: "Godkännande",
    
    // Add Project Form
    createNewProject: "Skapa Nytt Projekt",
    fillDetails: "Fyll i detaljerna nedan för att skapa ett nytt due diligence-projekt.",
    projectTargetName: "Namn",
    enterProjectName: "Namn",
    selectStatus: "Välj status",
    selectPhase: "Välj fas",
    enterDealValue: "Ange förvärvsvärde",
    selectRiskLevel: "Välj risknivå",
    projectSector: "Sektor",
    selectSector: "Välj sektor",
    projectDealType: "Affärstyp",
    selectDealType: "Välj affärstyp",
    dealTypeAcquisition: "Förvärv",
    dealTypeMerger: "Fusion",
    dealTypeGrowthInvestment: "Tillväxtinvestering",
    dealTypePEBuyout: "PE-uppköp",
    dealTypeCarveOut: "Carve-out",
    dealTypeJointVenture: "Joint venture",
    dealTypeAssetPurchase: "Tillgångsköp",
    numberOfRedFlags: "Antal redigeringsnyckelord",
    selectOwner: "Välj ägare",
    createProject: "Skapa Projekt",
    projectCreated: "Projekt Skapat",
    projectCreatedDesc: "Projektet har skapats framgångsrikt.",
    projectDeleted: "Projekt Raderat",
    projectDeletedDesc: "Projektet har raderats.",
    confirmDeleteProject: "Radera Projekt",
    confirmDeleteProjectDesc: "Är du säker på att du vill radera",
    deleteHasCreditsError: "Det går inte att radera ett projekt som har ett positivt kreditsaldo. Överför eller använd krediterna först.",
    projectUpdated: "Projekt Uppdaterat",
    projectUpdatedDesc: "Projektet har uppdaterats.",
    
    // Users & Roles
    usersRolesTitle: "Användare & Rollhantering",
    firstName: "Förnamn",
    lastName: "Efternamn",
    email: "E-post",
    role: "Roll",
    department: "Avdelning",
    raci: "RACI",
    bm: "BM",
    responsible: "Ansvarig",
    accountable: "Beslutande",
    consulted: "Konsulterad",
    informed: "Informerad",
    allRoles: "Alla Roller",
    allDepartments: "Alla Avdelningar",
    searchUsers: "Sök användare...",
    noUsersFound: "Inga användare hittades som matchar dina kriterier.",
    
    // Auth
    welcomeBack: "Välkommen Tillbaka!",
    signInCredentials: "Ange dina uppgifter för att logga in",
    createAccount: "Skapa Konto",
    createAccountSubtitle: "Skapa ditt konto för att komma igång",
    signIn: "Logga In",
    signUp: "Registrera",
    emailAddress: "E-postadress",
    password: "Lösenord",
    confirmPassword: "Bekräfta Lösenord",
    fullName: "Fullständigt Namn",
    rememberMe: "Kom ihåg mig",
    forgotPassword: "Glömt lösenord?",
    noAccount: "Har du inget konto?",
    haveAccount: "Har du redan ett konto?",
    orContinueWith: "Eller",
    agreeTerms: "Jag godkänner",
    termsConditions: "Villkor och Bestämmelser",
    signingIn: "Loggar in...",
    creatingAccount: "Skapar konto...",
    continueWithGoogle: "Fortsätt med Google",
    continueWithMicrosoft: "Fortsätt med Microsoft",
    continueWithApple: "Fortsätt med Apple",
    supabaseRequired: "Anslut Supabase för att aktivera OAuth-inloggning",
    
    // Welcome
    welcomeToVeritas: "Välkommen till Perseon Labs - Veritas",
    welcomeDescription: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    
    // New keys
    progress: "Framsteg",
    findings: "Fynd",
    tabOverview: "Översikt",
    tabWorkstreams: "Arbetsströmmar",
    tabTasks: "Uppgifter",
    tabDocuments: "Dokument",
    tabQA: "Frågor & Svar",
    tabRisks: "Risker/Fynd",
    tabApprovals: "Godkännanden",
    projectTimeline: "Projekttidslinje",
    workstream: "Arbetsström",
    allOwners: "Alla ägare",
    wsIT: "IT",
    wsCommercial: "Kommersiell",
    wsOperations: "Drift",
    wsCyber: "Cybersäkerhet",
    wsIP: "IP",
    wsNotStarted: "Ej Startad",
    wsInProgress: "Pågående",
    wsCompleted: "Slutförd",
    wsBlocked: "Blockerad",
    agentCybersecurity: "Cybersäkerhet",
    agentInfrastructureCloud: "Infrastruktur & Moln",
    agentSoftwareEngineering: "Mjukvaruutveckling",
    agentDataAI: "Data & AI",
    agentCompliance: "Efterlevnad",
    agentVendorThirdParty: "Leverantör & Tredje Part",
    agentIpLicensing: "IP & Licensiering",
    agentArchitectureScalability: "Arkitektur & Skalbarhet",
    agentIncidentOps: "Incident & Drift",
    agentPeopleOrg: "Personal & Organisation",
    agentFinancialDueDiligence: "Finansiell due diligence",
    agentSummarization: "Sammanfattning",
    agentContradictionDetector: "Motsägelsedetektering",
    agentRedFlagClassifier: "Rödflaggklassificering",
    agentGapDetector: "Gapdetektering",
    agentCrossDocSynthesis: "Dokumentsyntes",
    wsPending: "Väntande",
    wsRunning: "Körs",
    wsFailed: "Misslyckad",
    riskScore: "Riskpoäng",
    confidence: "Konfidens",
    keyGaps: "Viktiga Luckor",
    severity: "Allvarlighet",
    documentType: "Dokumenttyp",
    recommendation: "Rekommendation",
    evidence: "Bevis",
    noWorkstreams: "Inga arbetsströmmar hittades för detta projekt",
    noFindings: "Inga fynd tillgängliga",
    crossAnalysisFindings: "Korsanalysresultat",
    summary: "Sammanfattning",
    taskKanban: "Uppgiftstavla",
    addTask: "Lägg till Uppgift",
    taskTodo: "Att Göra",
    taskInProgress: "Pågående",
    taskReview: "Granskning",
    taskDone: "Klar",
    searchDocuments: "Sök dokument...",
    allTags: "Alla Taggar",
    allStatuses: "Alla Statusar",
    documentName: "Dokumentnamn",
    tag: "Tagg",
    version: "Version",
    reviewStatus: "Granskningsstatus",
    uploadedBy: "Uppladdad av",
    uploadedAt: "Uppladdad",
    docPending: "Väntande",
    docInReview: "Under Granskning",
    docApproved: "Godkänd",
    docRejected: "Avvisad",
    documentDeleted: "Dokument Raderat",
    documentDeletedDesc: "Dokumentet har raderats.",
    totalQuestions: "Totala Frågor",
    qaOpen: "Öppen",
    qaPending: "Väntande",
    qaAnswered: "Besvarad",
    qaClosed: "Stängd",
    allWorkstreams: "Alla Arbetsströmmar",
    addQuestion: "Lägg till Fråga",
    askedOn: "Ställd",
    answer: "Svar",
    enterAnswer: "Skriv ditt svar...",
    submitAnswer: "Skicka Svar",
    totalRisks: "Totala Risker",
    riskCritical: "Kritisk",
    riskHigh: "Hög Risk",
    riskMedium: "Medel Risk",
    riskLow: "Låg Risk",
    riskInfo: "Info",
    riskHeatmap: "Riskvärmekarta",
    riskRegister: "Riskregister",
    addRisk: "Lägg till Risk",
    riskTitle: "Risk",
    score: "Poäng",
    likelihood: "Sannolikhet",
    impact: "Påverkan",
    riskIdentified: "Identifierad",
    riskMitigating: "Åtgärdas",
    riskMitigated: "Åtgärdad",
    riskAccepted: "Accepterad",
    approvalGates: "Godkännandegrindar",
    gateDetails: "Grinddetaljer",
    decidedBy: "Beslutad av",
    conditions: "Villkor",
    pending: "Väntande",
    go: "Godkänn",
    noGo: "Avslå",
    conditional: "Villkorligt",
    auditTrail: "Revisionslogg",
    activityLog: "Aktivitetslogg",
    justNow: "Just nu",
    ago: "sedan",
    
    // Team & Roles Tab
    tabTeamRoles: "Team & Roller",
    tabFolders: "Mappar",
    tabPayments: "Transaktioner",

    // Payments
    buyCredits: "Köp krediter",
    creditPackages: "Kreditpaket",
    selectPackage: "Välj ett paket",
    credits: "krediter",
    paymentHistory: "Transaktionshistorik",
    paymentDate: "Datum",
    paymentStatus: "Status",
    paymentAmount: "Belopp",
    paymentCredits: "Krediter",
    paymentPending: "Väntande",
    paymentCompleted: "Genomförd",
    paymentFailed: "Misslyckad",
    paymentRefunded: "Återbetald",
    paymentCancelled: "Avbruten",
    noPayments: "Inga transaktioner ännu",
    checkoutSuccess: "Betalningen lyckades! Krediter har lagts till i ditt projekt.",
    checkoutCancel: "Betalningen avbröts.",
    transferCredits: "Överför krediter",
    transferCreditsTitle: "Överför krediter",
    transferCreditsDescription: "Flytta krediter från denna organisation till en annan organisation du äger.",
    transferTargetProject: "Målorganisation",
    transferSelectTarget: "Välj en organisation",
    transferAmount: "Belopp",
    transferSend: "Skicka",
    transferSuccess: "Krediterna överfördes.",
    transferError: "Det gick inte att överföra krediter.",
    transferInsufficientCredits: "Inte tillräckligt med krediter tillgängliga.",
    transferNoTargets: "Du äger inga andra organisationer att överföra krediter till.",

    // Seats
    seats: "Platser",
    seatsActive: "Aktiva",
    seatsAvailable: "Tillgängliga",
    buySeats: "Köp platser",
    seatPlans: "Platspaket",
    selectSeatPlan: "Välj ett paket",
    seatPlanBonusCredits: "krediter bonus",
    paymentType: "Typ",
    paymentTypeCredits: "Krediter",
    paymentTypeSeats: "Platser",
    seatStatus: "Plats",
    seatActive: "Aktiv",
    seatInactive: "Inaktiv",
    activateSeat: "Aktivera plats",
    deactivateSeat: "Inaktivera plats",
    noSeatsAvailableTooltip: "Inga lediga platser i poolen",
    seatEntitlementBlockedMessage: "Din organisation behöver fler aktiva platser för att göra detta. Be en ägare eller redaktör att köpa eller aktivera platser.",
    folders: "Mappar",
    newFolder: "Ny mapp",
    newSubfolder: "Ny undermapp",
    folderName: "Mappnamn",
    createFolder: "Skapa mapp",
    renameFolder: "Byt namn på mapp",
    deleteFolder: "Ta bort mapp",
    deleteFolderConfirm: "Är du säker på att du vill ta bort den här mappen? Åtgärden kan inte ångras.",
    folderHasChildren: "Det går inte att ta bort en mapp som innehåller undermappar. Ta bort undermapparna först.",
    noFolders: "Inga mappar än. Skapa en för att komma igång.",
    uploadFile: "Ladda upp fil",
    fileTooLarge: "Filen överskrider gränsen på 50 MB.",
    fileTypeNotAllowed: "Filtypen är inte tillåten. Endast dokument, textfiler och källkodsfiler är tillåtna.",
    deleteFile: "Ta bort fil",
    deleteFileConfirm: "Är du säker på att du vill ta bort den här filen? Åtgärden kan inte ångras.",
    teamRoles: "Team & Roller",
    totalMembers: "Totalt Medlemmar",
    addTeamMember: "Lägg till Teammedlem",
    addTeamMemberDesc: "Sök och lägg till en användare i projektteamet.",
    importFromDirectory: "Importera från Katalog",
    importFromDirectoryDesc: "Välj flera användare från den globala katalogen att lägga till i projektet.",
    searchTeamMembers: "Sök teammedlemmar...",
    projectRole: "Projektroll",
    projectDepartment: "Projektavd",
    permission: "Behörighet",
    allPermissions: "Alla Behörigheter",
    allRaci: "Alla RACI",
    projectAdmin: "Projektadmin",
    projectMember: "Medlem",
    projectViewer: "Läsare",
    memberAdded: "Medlem Tillagd",
    memberAddedDesc: "Användaren har lagts till i projektteamet.",
    memberRemoved: "Medlem Borttagen",
    memberRemovedDesc: "Användaren har tagits bort från projektteamet.",
    membersImported: "Medlemmar Importerade",
    membersImportedDesc: "användare har lagts till i projektteamet.",
    resetToGlobal: "Återställ till Global",
    resetToGlobalDesc: "Projektöverskridningar har återställts till globala standardvärden.",
    removeMember: "Ta bort Medlem",
    noMembersFound: "Inga teammedlemmar hittades.",
    noUsersAvailable: "Inga användare tillgängliga att lägga till.",
    allUsersAdded: "Alla användare har lagts till i projektet.",
    roleOwner: "Ägare",
    roleEditor: "Redigerare",
    addUserByEmail: "Lägg till Användare via E-post",
    enterUserEmail: "Ange användarens e-postadress...",
    roleChanged: "Roll Uppdaterad",
    roleChangedDesc: "Användarens roll har ändrats.",
    confirmRemoveUser: "Ta bort användare från projektet?",
    confirmRemoveUserDesc: "Detta återkallar användarens åtkomst till projektet. Åtgärden kan inte ångras.",
    usersSelected: "användare valda",
    manageFolderUsers: "Hantera mapanvändare",
    addFolderMember: "Lägg till medlem",
    selectProjectUser: "Välj en projektmedlem...",
    noFolderMembers: "Inga användare har tilldelats den här mappen.",
    confirmRemoveFolderUser: "Ta bort användare från mappen?",
    confirmRemoveFolderUserDesc: "Detta återkallar användarens åtkomst på mappnivå. Åtgärden kan inte ångras.",
    importUsers: "Importera Valda",
    global: "Global",
    name: "Namn",
    addToProject: "Lägg till i Projekt",
    selectProject: "Välj Projekt",
    useGlobalDefaults: "Använd globala standardvärden",
    
    // Dashboard Customization
    customize: "Anpassa",
    customizeDashboard: "Anpassa Dashboard",
    customizeDashboardDesc: "Välj vilka widgets som ska visas på din dashboard",
    enableAll: "Aktivera Alla",
    disableAll: "Inaktivera Alla",
    resetToDefault: "Återställ till Standard",
    customizationDone: "Klar",
    
    // Reports & Insights
    reportsSubtitle: "Rapporter, insikter och analyser",
    exportBtn: "Exportera",
    exportSections: "Avsnitt att exportera",
    selectAll: "Markera alla",
    riskCompliance: "Risk & Compliance",
    portfolioAnalysis: "Portföljanalys",
    complianceAudit: "Compliance & Audit",
    customReports: "Anpassade Rapporter",
    riskHeatmapTitle: "Risk Heatmap",
    riskDistribution: "Riskfördelning per kategori",
    riskTrends: "Risktrender",
    increasingDecreasing: "Ökande/minskande risk över tid",
    policyDeviations: "Policyavvikelser",
    deviationsFromPolicy: "Avvikelser från policy och tröskelvärden",
    projectLabel: "Projekt",
    policyLabel: "Policy",
    deviationLabel: "Avvikelse",
    severityLabel: "Allvarlighetsgrad",
    lastMonth: "Förra månaden",
    thisMonth: "Denna månad",
    trend: "Trend",
    it: "IT",
    critical: "Kritisk",
    projectVsProject: "Projekt vs Projekt",
    compareProjects: "Jämför projekt baserat på risk och konfidens",
    confidenceScore: "Konfidenspoäng",
    benchmarkScore: "Benchmarkpoäng",
    riskVsConfidence: "Risk vs Konfidensmatris",
    projectPositioning: "Positionering av projekt",
    vsLastQuarter: "vs förra kvartalet",
    youAreBetter: "Ni ligger bättre",
    checklistCompletion: "Checklist Completion Rate",
    completionByCategory: "Slutförande av checklistor per kategori",
    decisionHistory: "Beslutshistorik",
    whoApprovedWhat: "Vem godkände vad – när",
    fullAuditTrail: "Full Audit Trail",
    completeTraceability: "Komplett spårbarhet för alla aktiviteter",
    timestamp: "Tidpunkt",
    userLabel: "Användare",
    actionLabel: "Åtgärd",
    objectLabel: "Objekt",
    approved: "Godkänd",
    rejected: "Avvisad",
    submitted: "Inskickad",
    updated: "Uppdaterad",
    savedTemplates: "Sparade Mallar",
    yourTemplates: "Dina rapportmallar",
    lastUsed: "Senast använd",
    newTemplate: "Ny Mall",
    buildOwnReport: "Bygg Egen Rapport",
    selectKpis: "Välj KPI:er",
    selectChartType: "Välj Diagramtyp",
    barChart: "Stapel",
    lineChart: "Linje",
    pieChart: "Cirkel",
    heatmapChart: "Heatmap",
    dataSources: "Datakällor",
    allProjects: "Alla projekt",
    activeProjectsFilter: "Aktiva projekt",
    completedProjects: "Avslutade projekt",
    last30Days: "Senaste 30 dagar",
    lastQuarter: "Senaste kvartalet",
    lastYear: "Senaste året",
    preview: "Förhandsgranska",
    saveAsTemplate: "Spara som Mall",
    completionRate: "Slutförandegrad",
    riskCount: "Antal risker",
    approvals: "Godkännanden",
    review: "Granskning",

    // Settings
    settings: "Inställningar",
    settingsTitle: "Användarinställningar",
    settingsSubtitle: "Hantera din profil, utseende och språkinställningar",
    profileSettings: "Profil",
    displayName: "Visningsnamn",
    displayNamePlaceholder: "Ange ditt visningsnamn",
    themeSettings: "Utseende",
    themeLight: "Ljust",
    themeDark: "Mörkt",
    themeSystem: "System",
    languageSettings: "Språk",
    settingsSaved: "Inställningar sparade",
    settingsSavedDesc: "Dina inställningar har uppdaterats.",
    settingsError: "Det gick inte att spara inställningar. Försök igen.",

    // Organizations
    organization: "Organisation",
    organizations: "Organisationer",
    organizationsSubtitle: "Hantera organisationer, medlemmar, projekt och krediter.",
    addOrganization: "Lägg till organisation",
    createNewOrganization: "Skapa ny organisation",
    fillOrganizationDetails: "Fyll i organisationsuppgifter.",
    organizationName: "Organisationsnamn",
    description: "Beskrivning",
    organizationNumber: "Organisationsnummer",
    contactEmail: "Kontakt-e-post",
    country: "Land",
    organizationOwner: "Organisationsägare",
    createOrganization: "Skapa organisation",
    organizationNotFound: "Organisationen hittades inte",
    organizationCreditBalance: "Organisationens kreditsaldo",
    organizationProjects: "Projekt",
    organizationUsers: "Användare",
    organizationDetails: "Organisationsdetaljer",
    lastUpdated: "Senast uppdaterad",
    createdAt: "Skapad",
    viewProjects: "Visa projekt",
    searchOrganizations: "Sök organisationer...",
    openOrganization: "Öppna organisation",
    noOrganizationsFound: "Inga organisationer hittades",
    confirmDeleteOrganization: "Radera organisation?",
    confirmDeleteOrganizationDesc: "Detta raderar permanent",
    organizationDeleteBlocked: "Organisationer kan bara raderas när kreditsaldot är 0 och inga projekt tillhör dem.",
    selectOrganization: "Välj organisation",
    createOrganizationFirst: "Skapa en organisation innan du lägger till ett projekt.",
    addOrganizationMemberDesc: "Lägg till en organisationsmedlem via e-post.",
    inviteMember: "Bjud in",
    inviteMemberDesc: "Bjud in en användare till organisationen via e-post. De måste acceptera inbjudan innan de kan komma åt organisationens data.",
    pendingInvitationBadge: "Väntande",
    pendingInvitationsTitle: "Väntande inbjudningar",
    acceptInvitation: "Acceptera",
    declineInvitation: "Avböj",
    invitedToJoin: "bjöd in dig till",
    organizationMembersLoadError: "Kunde inte läsa in organisationsmedlemmar.",
    confirmRemoveOrganizationUserDesc: "Ta bort användaren från organisationen?",
    addExistingProject: "Lägg till befintligt projekt",
    moveProjectWarning: "Att flytta ett projekt ändrar vilken organisation som äger dess krediter och behörighetskontext.",
    moveProject: "Flytta projekt",
    searchProjectsPlaceholder: "Sök projekt efter namn...",
    noMatchingProjects: "Inga matchande projekt hittades.",
    searchToFindProjects: "Sök för att hitta ett projekt du kan lägga till i denna organisation.",
    transferTargetOrganization: "Målorganisation",
    previous: "Föregående",
    next: "Nästa",

    // Report Generation
    generateReport: "Kör agenter",
    generatingReport: "Kör...",
    reportGenerated: "AI-arbetsflöden slutförda",
    confirmGenerateReport: "Bekräfta körning av agenter",
    confirmGenerateReportDesc: "Är du säker på att du vill köra AI-arbetsflöden för detta projekt? Alla uppladdade filer kommer att bearbetas.",
    projectCreditBalance: "Kreditsaldo",
    creditsUsableUntil: "Krediter kan användas till",
    creditsUsableUntilUnavailable: "Saknar datum för krediternas giltighet",
    requiredCredits: "Minsta krävda krediter",
    insufficientProjectCredits: "Otillräckliga krediter för denna körning.",

    // Technical Documentation Guide
    technicalDocumentation: "Guide",
    techDocTitle: "Due Diligence-guide",
    techDocSubtitle: "En heltäckande checklista över dokumentation som en köpare bör kräva av en säljare vid due diligence.",
    techDocIntro: "Använd den här guiden för att säkerställa att du har fått och granskat all viktig dokumentation från målföretaget innan du slutför förvärvet.",
    techDocRequired: "Krävs",
    techDocRecommended: "Rekommenderas",
    techDocS1Title: "Systemarkitektur",
    techDocS1Desc: "Dokumentation som beskriver hur systemet är strukturerat, hur komponenter interagerar och vilka teknologier som används.",
    techDocS1I1: "Övergripande arkitekturdiagram (monolit, mikrotjänster, serverlöst)",
    techDocS1I2: "Komponent- och tjänsteinteraktionsdiagram",
    techDocS1I3: "Dokumentation av teknikstack (språk, ramverk, databaser)",
    techDocS1I4: "Dataflödes- och integrationsdiagram",
    techDocS2Title: "Källkod & Versionshantering",
    techDocS2Desc: "Bevis på hur källkod hanteras, granskas och underhålls.",
    techDocS2I1: "Åtkomst till källkodsrepositories (GitHub, GitLab, Bitbucket)",
    techDocS2I2: "Förgreningsstrategi och dokumentation av releaseprocessen",
    techDocS2I3: "Policyer för kodgranskning och kvalitetsstandarder",
    techDocS2I4: "Inventering av tredjepartsbibliotek och beroenden med licenser",
    techDocS3Title: "Infrastruktur & Moln",
    techDocS3Desc: "Dokumentation som täcker hostingmiljöer, molnkonfiguration och nätverksarkitektur.",
    techDocS3I1: "Hosting- och molninfrastrukturdokumentation (AWS, Azure, GCP)",
    techDocS3I2: "Infrastruktur-som-kod-skript (Terraform, CloudFormation, Pulumi)",
    techDocS3I3: "Nätverkstopologi och arkitekturdiagram",
    techDocS3I4: "Skalbarkhetsarkitektur och konfiguration av lastbalansering",
    techDocS4Title: "CI/CD & DevOps",
    techDocS4Desc: "Processer och verktyg som används för att bygga, testa och driftsätta programvara tillförlitligt.",
    techDocS4I1: "CI/CD-pipeline-dokumentation och verktyg (Jenkins, GitHub Actions, m.fl.)",
    techDocS4I2: "Driftsättningsprocedurer och återställningsplaner",
    techDocS4I3: "Miljökonfiguration (utveckling, testmiljö, produktion)",
    techDocS4I4: "Releasekadans, versionspolicy och ändringslogghistorik",
    techDocS5Title: "Databas & Datahantering",
    techDocS5Desc: "Dokumentation som visar att data är välstrukturerat, säkerhetskopierat och återställningsbart.",
    techDocS5I1: "Databasschema och entitets-relationsdiagram (ER)",
    techDocS5I2: "Policyer för datalagring och arkivering",
    techDocS5I3: "Procedurer för säkerhetskopiering och återhämtning med testresultat",
    techDocS5I4: "Historik och verktysdokumentation för datamigrering",
    techDocS6Title: "Säkerhet & Åtkomstkontroll",
    techDocS6Desc: "Policyer och kontroller som skyddar systemet mot obehörig åtkomst och attacker.",
    techDocS6I1: "Dokumentation för identitets- och åtkomsthantering (IAM)",
    techDocS6I2: "Metod för hantering av hemligheter (valv, nyckelrotationspolicyer)",
    techDocS6I3: "Nätverkssäkerhetsdokumentation (brandväggar, VPN, segmentering)",
    techDocS6I4: "Dokumentation av säkerhetspolicyer och procedurer",
    techDocS7Title: "Efterlevnad & Certifieringar",
    techDocS7Desc: "Formella certifieringar och revisionsrapporter som visar efterlevnad av regler och säkerhetskrav.",
    techDocS7I1: "Säkerhetscertifieringar (ISO 27001, SOC 2 Typ II, PCI-DSS)",
    techDocS7I2: "Dokumentation för efterlevnad av integritetslagar (GDPR, CCPA, PIPEDA)",
    techDocS7I3: "Penetrationstestrapporter från de senaste 12–24 månaderna",
    techDocS7I4: "Historik för sårbarhetshantering och patchningskadans",
    techDocS8Title: "API & Tredjepartsintegrationer",
    techDocS8Desc: "Dokumentation av hur systemet exponerar data och ansluter till externa tjänster.",
    techDocS8I1: "API-specifikationer och dokumentation (OpenAPI/Swagger)",
    techDocS8I2: "Inventering av tredjepartsintegrationer med leverantörers SLA:er",
    techDocS8I3: "Dokumentation för autentisering och auktorisering (OAuth, SSO, API-nycklar)",
    techDocS8I4: "Dokumentation för webhooks och händelsedrivna integrationer",
    techDocS9Title: "Katastrofåterställning & Affärskontinuitet",
    techDocS9Desc: "Planer och bevis på att systemet kan återhämta sig från fel och fortsätta fungera.",
    techDocS9I1: "Plan för katastrofåterställning (DR) och affärskontinuitetsplan (BCP)",
    techDocS9I2: "RTO/RPO-mål och historiska testresultat",
    techDocS9I3: "Procedurer för incidenthantering och eskaleringsdokumentation",
    techDocS9I4: "Incidentrapporter från de senaste 24 månaderna",
    techDocS10Title: "Teknisk skuld & Färdplan",
    techDocS10Desc: "Transparens kring kända begränsningar, framtida planer och skyldigheter för öppen källkod.",
    techDocS10I1: "Inventering och prioritering av känd teknisk skuld",
    techDocS10I2: "Lista över föråldrade eller avvecklade komponenter och bibliotek",
    techDocS10I3: "Produkt- och teknisk färdplan (12–24 månader)",
    techDocS10I4: "Skyldigheter och efterlevnad av öppen källkodslicenser",
    techDocS11Title: "Finansiella rapporter & Revision",
    techDocS11Desc: "Reviderade finansiella rapporter och oberoende granskningsrapporter som täcker de senaste tre till fem räkenskapsåren.",
    techDocS11I1: "Reviderade årsredovisningar (resultaträkning, balansräkning, kassaflöde) för de senaste 3–5 åren",
    techDocS11I2: "Revisionsberättelser och management letters från oberoende revisor",
    techDocS11I3: "Delårsbokslut och interna ledningsrapporter (innevarande år)",
    techDocS11I4: "Redovisningsprinciper samt eventuella omräkningar eller justeringar av tidigare år",
    techDocS12Title: "Intäkter & Affärsmodell",
    techDocS12Desc: "Detaljerad uppdelning av intäktsströmmar, kundkoncentration och affärsmodellens hållbarhet.",
    techDocS12I1: "Intäktsfördelning per produkt, tjänstegren, geografi och kundsegment",
    techDocS12I2: "Topp-10-kundlista med avtalstider, förnyelsedatum och churn-data",
    techDocS12I3: "Analys av återkommande kontra engångsintäkter samt kohortbaserad retentionsdata",
    techDocS12I4: "Dokumentation av prismodell och historiska prisförändringar",
    techDocS13Title: "Rörelsekapital & Kassaflöde",
    techDocS13Desc: "Analys av bolagets likviditet, kassaflödesgenerering och befintliga skuldåtaganden.",
    techDocS13I1: "Rörelsekapitalanalys (kundfordringar/leverantörsskulder, lagernivåer, dagar utestående)",
    techDocS13I2: "Historiska och prognostiserade kassaflödesanalyser med nyckelförutsättningar",
    techDocS13I3: "Skuldschema, kreditfaciliteter, covenants och eventuellt pantsatta tillgångar",
    techDocS13I4: "Historik för investeringsutgifter (CapEx) samt uppdelning i underhålls- respektive tillväxtskapex",
    techDocS14Title: "Skatte- & Juridiska Åtaganden",
    techDocS14Desc: "Översikt av skatteefterlevnad, utestående skulder samt eventuella väsentliga juridiska eller regulatoriska exponeringar.",
    techDocS14I1: "Inlämnade skattedeklarationer för de senaste 3–5 åren (bolagsskatt, moms)",
    techDocS14I2: "Pågående skattetvister, skattebeslut eller öppna revisionsärenden",
    techDocS14I3: "Förteckning över eventualförpliktelser, avsättningar och poster utanför balansräkningen",
    techDocS14I4: "Väsentliga avtal, pågående rättsprocesser och regulatorisk korrespondens",
  },

  no: {
    // Common
    search: "Søk...",
    signOut: "Logg ut",
    admin: "Admin",
    user: "Bruker",
    save: "Lagre",
    cancel: "Avbryt",
    create: "Opprett",
    edit: "Rediger",
    delete: "Slett",
    filter: "Filtrer",
    actions: "Handlinger",
    noResults: "Ingen resultater funnet",

    // Project Detail
    projectDetails: "Prosjektdetaljer",
    projectNotFound: "Prosjekt ikke funnet",
    unlockEdit: "Lås opp & Rediger",
    projectSaved: "Prosjekt Lagret",
    projectSavedDesc: "Prosjektet har blitt oppdatert.",
    documents: "Dokumenter",
    uploadDocuments: "Last opp Dokumenter",
    documentsUploaded: "Dokumenter Lastet Opp",
    documentsUploadedDesc: "dokumenter har blitt lastet opp.",
    noDocuments: "Ingen dokumenter lastet opp ennå.",

    // Navigation
    dashboard: "Oversikt",
    project: "Prosjekt",
    projects: "Prosjekter",
    tasks: "Oppgaver/Handlinger",
    reports: "Rapporter & Innsikt",
    usersRoles: "Brukere & Roller",

    // Dashboard
    dashboardTitle: "Oversikt",
    dashboardSubtitle: "Følg din due diligence-pipeline og fremdrift",
    totalProjects: "Totale Prosjekter",
    activeProjects: "Aktive Prosjekter",
    totalDealValue: "Total Prosjektverdi",
    avgDDProgress: "Gjennomsnittlig DD-fremdrift",
    fromLastMonth: "fra forrige måned",
    projectReports: "Prosjektrapporter",
    categoryBreakdown: "Kategorifordeling",

    // Project / Due Diligence
    dueDiligence: "Due Diligence",
    addProject: "Legg til Prosjekt",
    projectTarget: "Målnavn",
    status: "Status",
    phase: "Fase",
    dealValue: "Prosjektverdi",
    ddProgress: "Fremdrift",
    risk: "Risiko",
    redFlags: "Røde flagg",
    redacted: "Redigert innhold",
    showRedacted: "Vis redigert innhold",
    hideRedacted: "Skjul redigert innhold",
    redactedCommaSeparated: "Skill flere nøkkelord med komma",
    expectedCloseDate: "Forventet sluttdato",
    owner: "Eier",
    openProject: "Åpne Prosjekter",
    noProjectsFound: "Ingen prosjekter funnet som matcher filtrene.",

    // Status
    active: "Aktiv",
    onHold: "På Vent",
    cancelled: "Kansellert",
    done: "Ferdig",

    // Risk
    low: "Lav",
    medium: "Middels",
    high: "Høy",

    // Phases
    initialReview: "Innledende Gjennomgang",
    finalReview: "Sluttgjennomgang",
    negotiation: "Forhandling",
    closing: "Godkjenning",

    // Add Project Form
    createNewProject: "Opprett Nytt Prosjekt",
    fillDetails: "Fyll inn detaljene nedenfor for å opprette et nytt due diligence-prosjekt.",
    projectTargetName: "Navn",
    enterProjectName: "Navn",
    selectStatus: "Velg status",
    selectPhase: "Velg fase",
    enterDealValue: "Skriv inn oppkjøpsverdi",
    selectRiskLevel: "Velg risikonivå",
    projectSector: "Sektor",
    selectSector: "Velg sektor",
    projectDealType: "Avtalstype",
    selectDealType: "Velg avtalstype",
    dealTypeAcquisition: "Oppkjøp",
    dealTypeMerger: "Fusjon",
    dealTypeGrowthInvestment: "Vekstinvestering",
    dealTypePEBuyout: "PE-oppkjøp",
    dealTypeCarveOut: "Carve-out",
    dealTypeJointVenture: "Joint venture",
    dealTypeAssetPurchase: "Eiendelskjøp",
    numberOfRedFlags: "Antall redigeringsnøkkelord",
    selectOwner: "Velg eier",
    createProject: "Opprett Prosjekt",
    projectCreated: "Prosjekt Opprettet",
    projectCreatedDesc: "Prosjektet har blitt opprettet.",
    projectDeleted: "Prosjekt Slettet",
    projectDeletedDesc: "Prosjektet har blitt slettet.",
    confirmDeleteProject: "Slett Prosjekt",
    confirmDeleteProjectDesc: "Er du sikker på at du vil slette",
    deleteHasCreditsError: "Kan ikke slette et prosjekt som har en positiv kredittsaldo. Overfør eller bruk kredittene først.",
    projectUpdated: "Prosjekt Oppdatert",
    projectUpdatedDesc: "Prosjektet har blitt oppdatert.",

    // Users & Roles
    usersRolesTitle: "Brukere & Rolleadministrasjon",
    firstName: "Fornavn",
    lastName: "Etternavn",
    email: "E-post",
    role: "Rolle",
    department: "Avdeling",
    raci: "RACI",
    bm: "BM",
    responsible: "Ansvarlig",
    accountable: "Beslutningstaker",
    consulted: "Konsultert",
    informed: "Informert",
    allRoles: "Alle Roller",
    allDepartments: "Alle Avdelinger",
    searchUsers: "Søk brukere...",
    noUsersFound: "Ingen brukere funnet som matcher kriteriene.",

    // Auth
    welcomeBack: "Velkommen Tilbake!",
    signInCredentials: "Skriv inn påloggingsinformasjonen din",
    createAccount: "Opprett Konto",
    createAccountSubtitle: "Opprett kontoen din for å komme i gang",
    signIn: "Logg Inn",
    signUp: "Registrer",
    emailAddress: "E-postadresse",
    password: "Passord",
    confirmPassword: "Bekreft Passord",
    fullName: "Fullt Navn",
    rememberMe: "Husk meg",
    forgotPassword: "Glemt passord?",
    noAccount: "Har du ikke en konto?",
    haveAccount: "Har du allerede en konto?",
    orContinueWith: "Eller",
    agreeTerms: "Jeg godtar",
    termsConditions: "Vilkår og Betingelser",
    signingIn: "Logger inn...",
    creatingAccount: "Oppretter konto...",
    continueWithGoogle: "Fortsett med Google",
    continueWithMicrosoft: "Fortsett med Microsoft",
    continueWithApple: "Fortsett med Apple",
    supabaseRequired: "Koble til Supabase for å aktivere OAuth-innlogging",

    // Welcome
    welcomeToVeritas: "Velkommen til Perseon Labs - Veritas",
    welcomeDescription: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",

    // New keys
    progress: "Fremgang",
    findings: "Funn",
    tabOverview: "Oversikt",
    tabWorkstreams: "Arbeidsstrømmer",
    tabTasks: "Oppgaver",
    tabDocuments: "Dokumenter",
    tabQA: "Spørsmål & Svar",
    tabRisks: "Risikoer/Funn",
    tabApprovals: "Godkjenninger",
    projectTimeline: "Prosjekttidslinje",
    workstream: "Arbeidsstrøm",
    allOwners: "Alle eiere",
    wsIT: "IT",
    wsCommercial: "Kommersiell",
    wsOperations: "Drift",
    wsCyber: "Cybersikkerhet",
    wsIP: "IP",
    wsNotStarted: "Ikke Startet",
    wsInProgress: "Pågår",
    wsCompleted: "Fullført",
    wsBlocked: "Blokkert",
    agentCybersecurity: "Cybersikkerhet",
    agentInfrastructureCloud: "Infrastruktur & Sky",
    agentSoftwareEngineering: "Programvareutvikling",
    agentDataAI: "Data & AI",
    agentCompliance: "Etterlevelse",
    agentVendorThirdParty: "Leverandør & Tredjepart",
    agentIpLicensing: "IP & Lisensiering",
    agentArchitectureScalability: "Arkitektur & Skalerbarhet",
    agentIncidentOps: "Hendelse & Drift",
    agentPeopleOrg: "Personal & Organisasjon",
    agentFinancialDueDiligence: "Finansiell due diligence",
    agentSummarization: "Oppsummering",
    agentContradictionDetector: "Motsigelsedeteksjon",
    agentRedFlagClassifier: "Rødt flagg-klassifisering",
    agentGapDetector: "Gapdeteksjon",
    agentCrossDocSynthesis: "Dokumentsyntese",
    wsPending: "Ventende",
    wsRunning: "Kjører",
    wsFailed: "Mislykket",
    riskScore: "Risikopoeng",
    confidence: "Konfidens",
    keyGaps: "Viktige Mangler",
    severity: "Alvorlighet",
    documentType: "Dokumenttype",
    recommendation: "Anbefaling",
    evidence: "Bevis",
    noWorkstreams: "Ingen arbeidsstrømmer funnet for dette prosjektet",
    noFindings: "Ingen funn tilgjengelig",
    crossAnalysisFindings: "Kryssanalyseresultater",
    summary: "Sammendrag",
    taskKanban: "Oppgavetavle",
    addTask: "Legg til Oppgave",
    taskTodo: "Å Gjøre",
    taskInProgress: "Pågår",
    taskReview: "Gjennomgang",
    taskDone: "Ferdig",
    searchDocuments: "Søk dokumenter...",
    allTags: "Alle Tagger",
    allStatuses: "Alle Statuser",
    documentName: "Dokumentnavn",
    tag: "Tagg",
    version: "Versjon",
    reviewStatus: "Gjennomgangsstatus",
    uploadedBy: "Lastet opp av",
    uploadedAt: "Lastet opp",
    docPending: "Venter",
    docInReview: "Under Gjennomgang",
    docApproved: "Godkjent",
    docRejected: "Avvist",
    documentDeleted: "Dokument Slettet",
    documentDeletedDesc: "Dokumentet har blitt slettet.",
    totalQuestions: "Totale Spørsmål",
    qaOpen: "Åpen",
    qaPending: "Venter",
    qaAnswered: "Besvart",
    qaClosed: "Lukket",
    allWorkstreams: "Alle Arbeidsstrømmer",
    addQuestion: "Legg til Spørsmål",
    askedOn: "Stilt",
    answer: "Svar",
    enterAnswer: "Skriv inn svaret ditt...",
    submitAnswer: "Send Svar",
    totalRisks: "Totale Risikoer",
    riskCritical: "Kritisk",
    riskHigh: "Høy Risiko",
    riskMedium: "Middels Risiko",
    riskLow: "Lav Risiko",
    riskInfo: "Info",
    riskHeatmap: "Risikovarmekart",
    riskRegister: "Risikoregister",
    addRisk: "Legg til Risiko",
    riskTitle: "Risiko",
    score: "Poeng",
    likelihood: "Sannsynlighet",
    impact: "Påvirkning",
    riskIdentified: "Identifisert",
    riskMitigating: "Reduseres",
    riskMitigated: "Redusert",
    riskAccepted: "Akseptert",
    approvalGates: "Godkjenningsporter",
    gateDetails: "Portdetaljer",
    decidedBy: "Besluttet av",
    conditions: "Betingelser",
    pending: "Venter",
    go: "Godkjenn",
    noGo: "Avslå",
    conditional: "Betinget",
    auditTrail: "Revisjonslogg",
    activityLog: "Aktivitetslogg",
    justNow: "Akkurat nå",
    ago: "siden",

    // Team & Roles Tab
    tabTeamRoles: "Team & Roller",
    tabFolders: "Mapper",
    tabPayments: "Transaksjoner",

    // Payments
    buyCredits: "Kjøp kreditter",
    creditPackages: "Kredittpakker",
    selectPackage: "Velg en pakke",
    credits: "kreditter",
    paymentHistory: "Transaksjonshistorikk",
    paymentDate: "Dato",
    paymentStatus: "Status",
    paymentAmount: "Beløp",
    paymentCredits: "Kreditter",
    paymentPending: "Ventende",
    paymentCompleted: "Fullført",
    paymentFailed: "Mislykket",
    paymentRefunded: "Refundert",
    paymentCancelled: "Avbrutt",
    noPayments: "Ingen transaksjoner ennå",
    checkoutSuccess: "Betalingen var vellykket! Kreditter er lagt til prosjektet ditt.",
    checkoutCancel: "Betalingen ble avbrutt.",
    transferCredits: "Overfør kreditter",
    transferCreditsTitle: "Overfør kreditter",
    transferCreditsDescription: "Flytt kreditter fra denne organisasjonen til en annen organisasjon du eier.",
    transferTargetProject: "Målorganisasjon",
    transferSelectTarget: "Velg en organisasjon",
    transferAmount: "Beløp",
    transferSend: "Send",
    transferSuccess: "Kredittene ble overført.",
    transferError: "Kunne ikke overføre kreditter.",
    transferInsufficientCredits: "Ikke nok kreditter tilgjengelig.",
    transferNoTargets: "Du eier ingen andre organisasjoner å overføre kreditter til.",

    // Seats
    seats: "Seter",
    seatsActive: "Aktive",
    seatsAvailable: "Tilgjengelige",
    buySeats: "Kjøp seter",
    seatPlans: "Setepakker",
    selectSeatPlan: "Velg en pakke",
    seatPlanBonusCredits: "kreditter bonus",
    paymentType: "Type",
    paymentTypeCredits: "Kreditter",
    paymentTypeSeats: "Seter",
    seatStatus: "Sete",
    seatActive: "Aktiv",
    seatInactive: "Inaktiv",
    activateSeat: "Aktiver sete",
    deactivateSeat: "Deaktiver sete",
    noSeatsAvailableTooltip: "Ingen ledige seter i poolen",
    seatEntitlementBlockedMessage: "Organisasjonen din trenger flere aktive seter for å gjøre dette. Be en eier eller redaktør om å kjøpe eller aktivere seter.",

    folders: "Mapper",
    newFolder: "Ny mappe",
    newSubfolder: "Ny undermappe",
    folderName: "Mappenavn",
    createFolder: "Opprett mappe",
    renameFolder: "Gi nytt navn til mappe",
    deleteFolder: "Slett mappe",
    deleteFolderConfirm: "Er du sikker på at du vil slette denne mappen? Denne handlingen kan ikke angres.",
    folderHasChildren: "Kan ikke slette en mappe som inneholder undermapper. Slett undermappene først.",
    noFolders: "Ingen mapper ennå. Opprett en for å komme i gang.",
    uploadFile: "Last opp fil",
    fileTooLarge: "Filen overskrider grensen på 50 MB.",
    fileTypeNotAllowed: "Filtypen er ikke tillatt. Kun dokumenter, tekstfiler og kildekodefiler er tillatt.",
    deleteFile: "Slett fil",
    deleteFileConfirm: "Er du sikker på at du vil slette denne filen? Denne handlingen kan ikke angres.",
    teamRoles: "Team & Roller",
    totalMembers: "Totalt Medlemmer",
    addTeamMember: "Legg til Teammedlem",
    addTeamMemberDesc: "Søk og legg til en bruker i prosjektteamet.",
    importFromDirectory: "Importer fra Katalog",
    importFromDirectoryDesc: "Velg flere brukere fra den globale katalogen for å legge til i prosjektet.",
    searchTeamMembers: "Søk teammedlemmer...",
    projectRole: "Prosjektrolle",
    projectDepartment: "Prosjektavd",
    permission: "Tillatelse",
    allPermissions: "Alle Tillatelser",
    allRaci: "Alle RACI",
    projectAdmin: "Prosjektadmin",
    projectMember: "Medlem",
    projectViewer: "Leser",
    memberAdded: "Medlem Lagt til",
    memberAddedDesc: "Brukeren har blitt lagt til i prosjektteamet.",
    memberRemoved: "Medlem Fjernet",
    memberRemovedDesc: "Brukeren har blitt fjernet fra prosjektteamet.",
    membersImported: "Medlemmer Importert",
    membersImportedDesc: "brukere har blitt lagt til i prosjektteamet.",
    resetToGlobal: "Tilbakestill til Global",
    resetToGlobalDesc: "Prosjektoverrides har blitt tilbakestilt til globale standardverdier.",
    removeMember: "Fjern Medlem",
    noMembersFound: "Ingen teammedlemmer funnet.",
    noUsersAvailable: "Ingen brukere tilgjengelig for å legge til.",
    allUsersAdded: "Alle brukere har blitt lagt til i prosjektet.",
    roleOwner: "Eier",
    roleEditor: "Redaktør",
    addUserByEmail: "Legg til Bruker via E-post",
    enterUserEmail: "Skriv inn brukerens e-postadresse...",
    roleChanged: "Rolle Oppdatert",
    roleChangedDesc: "Brukerens rolle har blitt endret.",
    confirmRemoveUser: "Fjerne bruker fra prosjektet?",
    confirmRemoveUserDesc: "Dette vil tilbakekalle brukerens tilgang til prosjektet. Handlingen kan ikke angres.",
    usersSelected: "brukere valgt",
    manageFolderUsers: "Administrer mappbrukere",
    addFolderMember: "Legg til medlem",
    selectProjectUser: "Velg et prosjektmedlem...",
    noFolderMembers: "Ingen brukere er tildelt denne mappen.",
    confirmRemoveFolderUser: "Fjerne bruker fra mappen?",
    confirmRemoveFolderUserDesc: "Dette vil tilbakekalle brukerens tilgang på mappenivå. Handlingen kan ikke angres.",
    importUsers: "Importer Valgte",
    global: "Global",
    name: "Navn",
    addToProject: "Legg til i Prosjekt",
    selectProject: "Velg Prosjekt",
    useGlobalDefaults: "Bruk globale standardverdier",

    // Dashboard Customization
    customize: "Tilpass",
    customizeDashboard: "Tilpass Dashboard",
    customizeDashboardDesc: "Velg hvilke widgets som skal vises på dashboardet",
    enableAll: "Aktiver Alle",
    disableAll: "Deaktiver Alle",
    resetToDefault: "Tilbakestill til Standard",
    customizationDone: "Ferdig",

    // Reports & Insights
    reportsSubtitle: "Rapporter, innsikt og analyser",
    exportBtn: "Eksporter",
    exportSections: "Seksjoner som skal eksporteres",
    selectAll: "Velg alle",
    riskCompliance: "Risiko & Compliance",
    portfolioAnalysis: "Porteføljeanalyse",
    complianceAudit: "Compliance & Revisjon",
    customReports: "Tilpassede Rapporter",
    riskHeatmapTitle: "Risiko Heatmap",
    riskDistribution: "Risikofordeling per kategori",
    riskTrends: "Risikotrender",
    increasingDecreasing: "Økende/minkende risiko over tid",
    policyDeviations: "Policyavvik",
    deviationsFromPolicy: "Avvik fra policy og terskelverdier",
    projectLabel: "Prosjekt",
    policyLabel: "Policy",
    deviationLabel: "Avvik",
    severityLabel: "Alvorlighetsgrad",
    lastMonth: "Forrige måned",
    thisMonth: "Denne måned",
    trend: "Trend",
    it: "IT",
    critical: "Kritisk",
    projectVsProject: "Prosjekt vs Prosjekt",
    compareProjects: "Sammenlign prosjekter basert på risiko og konfidens",
    confidenceScore: "Konfidenspoeng",
    benchmarkScore: "Referansescore",
    riskVsConfidence: "Risiko vs Konfidensmatrise",
    projectPositioning: "Posisjonering av prosjekter",
    vsLastQuarter: "vs forrige kvartal",
    youAreBetter: "Dere ligger bedre",
    checklistCompletion: "Sjekkliste Fullføringsrate",
    completionByCategory: "Fullføring av sjekklister per kategori",
    decisionHistory: "Beslutningshistorikk",
    whoApprovedWhat: "Hvem godkjente hva – når",
    fullAuditTrail: "Full Revisjonslogg",
    completeTraceability: "Komplett sporbarhet for alle aktiviteter",
    timestamp: "Tidspunkt",
    userLabel: "Bruker",
    actionLabel: "Handling",
    objectLabel: "Objekt",
    approved: "Godkjent",
    rejected: "Avvist",
    submitted: "Innsendt",
    updated: "Oppdatert",
    savedTemplates: "Lagrede Maler",
    yourTemplates: "Dine rapportmaler",
    lastUsed: "Sist brukt",
    newTemplate: "Ny Mal",
    buildOwnReport: "Bygg Egen Rapport",
    selectKpis: "Velg KPIer",
    selectChartType: "Velg Diagramtype",
    barChart: "Stolpe",
    lineChart: "Linje",
    pieChart: "Sirkel",
    heatmapChart: "Heatmap",
    dataSources: "Datakilder",
    allProjects: "Alle prosjekter",
    activeProjectsFilter: "Aktive prosjekter",
    completedProjects: "Avsluttede prosjekter",
    last30Days: "Siste 30 dager",
    lastQuarter: "Siste kvartal",
    lastYear: "Siste år",
    preview: "Forhåndsvis",
    saveAsTemplate: "Lagre som Mal",
    completionRate: "Fullføringsrate",
    riskCount: "Antall risikoer",
    approvals: "Godkjenninger",
    review: "Gjennomgang",

    // Settings
    settings: "Innstillinger",
    settingsTitle: "Brukerinnstillinger",
    settingsSubtitle: "Administrer profil, utseende og språkinnstillinger",
    profileSettings: "Profil",
    displayName: "Visningsnavn",
    displayNamePlaceholder: "Skriv inn visningsnavnet ditt",
    themeSettings: "Utseende",
    themeLight: "Lyst",
    themeDark: "Mørkt",
    themeSystem: "System",
    languageSettings: "Språk",
    settingsSaved: "Innstillinger lagret",
    settingsSavedDesc: "Innstillingene dine er oppdatert.",
    settingsError: "Kunne ikke lagre innstillinger. Prøv igjen.",

    // Organizations
    organization: "Organisasjon",
    organizations: "Organisasjoner",
    organizationsSubtitle: "Administrer organisasjoner, medlemmer, prosjekter og kreditter.",
    addOrganization: "Legg til organisasjon",
    createNewOrganization: "Opprett ny organisasjon",
    fillOrganizationDetails: "Fyll inn organisasjonsdetaljer.",
    organizationName: "Organisasjonsnavn",
    description: "Beskrivelse",
    organizationNumber: "Organisasjonsnummer",
    contactEmail: "Kontakt-e-post",
    country: "Land",
    organizationOwner: "Organisasjonseier",
    createOrganization: "Opprett organisasjon",
    organizationNotFound: "Organisasjon ikke funnet",
    organizationCreditBalance: "Organisasjonens kredittsaldo",
    organizationProjects: "Prosjekter",
    organizationUsers: "Brukere",
    organizationDetails: "Organisasjonsdetaljer",
    lastUpdated: "Sist oppdatert",
    createdAt: "Opprettet",
    viewProjects: "Vis prosjekter",
    searchOrganizations: "Søk organisasjoner...",
    openOrganization: "Åpne organisasjon",
    noOrganizationsFound: "Ingen organisasjoner funnet",
    confirmDeleteOrganization: "Slett organisasjon?",
    confirmDeleteOrganizationDesc: "Dette sletter permanent",
    organizationDeleteBlocked: "Organisasjoner kan bare slettes når kredittsaldoen er 0 og de ikke har prosjekter.",
    selectOrganization: "Velg organisasjon",
    createOrganizationFirst: "Opprett en organisasjon før du legger til et prosjekt.",
    addOrganizationMemberDesc: "Legg til et organisasjonsmedlem via e-post.",
    inviteMember: "Inviter",
    inviteMemberDesc: "Inviter en bruker til denne organisasjonen via e-post. De må godta invitasjonen før de får tilgang til organisasjonens data.",
    pendingInvitationBadge: "Ventende",
    pendingInvitationsTitle: "Ventende invitasjoner",
    acceptInvitation: "Godta",
    declineInvitation: "Avslå",
    invitedToJoin: "inviterte deg til",
    organizationMembersLoadError: "Kunne ikke laste organisasjonsmedlemmer.",
    confirmRemoveOrganizationUserDesc: "Fjern denne brukeren fra organisasjonen?",
    addExistingProject: "Legg til eksisterende prosjekt",
    moveProjectWarning: "Flytting av et prosjekt endrer hvilken organisasjon som eier kredittene og tilgangskonteksten.",
    moveProject: "Flytt prosjekt",
    searchProjectsPlaceholder: "Søk etter prosjekter...",
    noMatchingProjects: "Ingen matchende prosjekter funnet.",
    searchToFindProjects: "Søk for å finne et prosjekt du kan legge til i denne organisasjonen.",
    transferTargetOrganization: "Målorganisasjon",
    previous: "Forrige",
    next: "Neste",

    // Report Generation
    generateReport: "Kjør agenter",
    generatingReport: "Kjører...",
    reportGenerated: "AI-arbeidsflyter fullført",
    confirmGenerateReport: "Bekreft kjøring av agenter",
    confirmGenerateReportDesc: "Er du sikker på at du vil kjøre AI-arbeidsflyter for dette prosjektet? Alle opplastede filer vil bli behandlet.",
    projectCreditBalance: "Kredittsaldo",
    creditsUsableUntil: "Kreditter kan brukes til",
    creditsUsableUntilUnavailable: "Mangler dato for kredittutløp",
    requiredCredits: "Minste påkrevde kreditter",
    insufficientProjectCredits: "Utilstrekkelige kreditter for denne kjøringen.",

    // Technical Documentation Guide
    technicalDocumentation: "Guide",
    techDocTitle: "Due Diligence-guide",
    techDocSubtitle: "En omfattende sjekkliste over dokumentasjon som en kjøper bør kreve av en selger ved due diligence.",
    techDocIntro: "Bruk denne guiden for å sikre at du har mottatt og gjennomgått all kritisk dokumentasjon fra målselskapet før du fullfører oppkjøpet.",
    techDocRequired: "Påkrevd",
    techDocRecommended: "Anbefalt",
    techDocS1Title: "Systemarkitektur",
    techDocS1Desc: "Dokumentasjon som beskriver hvordan systemet er strukturert, hvordan komponenter samhandler og hvilke teknologier som brukes.",
    techDocS1I1: "Overordnede arkitekturdiagrammer (monolit, mikrotjenester, serverløst)",
    techDocS1I2: "Komponent- og tjenesteinteraksjonsdiagrammer",
    techDocS1I3: "Dokumentasjon av teknologistakk (språk, rammeverk, databaser)",
    techDocS1I4: "Dataflyt- og integrasjonsdiagrammer",
    techDocS2Title: "Kildekode & Versjonshåndtering",
    techDocS2Desc: "Bevis på hvordan kildekode administreres, gjennomgås og vedlikeholdes.",
    techDocS2I1: "Tilgang til kildekoderepositories (GitHub, GitLab, Bitbucket)",
    techDocS2I2: "Forgreningsstrategi og dokumentasjon av releaseprosessen",
    techDocS2I3: "Retningslinjer for kodegjennomgang og kvalitetsstandarder",
    techDocS2I4: "Inventar over tredjepartsbiblioteker og avhengigheter med lisenser",
    techDocS3Title: "Infrastruktur & Sky",
    techDocS3Desc: "Dokumentasjon som dekker hostingmiljøer, skyoppsett og nettverksarkitektur.",
    techDocS3I1: "Hosting- og skyinfrastrukturdokumentasjon (AWS, Azure, GCP)",
    techDocS3I2: "Infrastruktur-som-kode-skript (Terraform, CloudFormation, Pulumi)",
    techDocS3I3: "Nettverkstopologi og arkitekturdiagrammer",
    techDocS3I4: "Skaleringsarkitektur og konfigurering av lastbalansering",
    techDocS4Title: "CI/CD & DevOps",
    techDocS4Desc: "Prosesser og verktøy som brukes for å bygge, teste og distribuere programvare pålitelig.",
    techDocS4I1: "CI/CD-pipeline-dokumentasjon og verktøy (Jenkins, GitHub Actions, m.fl.)",
    techDocS4I2: "Distribusjonsprosedyrer og tilbakestillingsplaner",
    techDocS4I3: "Miljøkonfigurasjon (utvikling, staging, produksjon)",
    techDocS4I4: "Releasekadanse, versjonspolicy og endringslogghistorikk",
    techDocS5Title: "Database & Datahåndtering",
    techDocS5Desc: "Dokumentasjon som beviser at data er godt strukturert, sikkerhetskopiert og gjenopprettbart.",
    techDocS5I1: "Databaseskjema og entitets-relasjonsdiagrammer (ER)",
    techDocS5I2: "Retningslinjer for datalagring og arkivering",
    techDocS5I3: "Prosedyrer for sikkerhetskopiering og gjenoppretting med testresultater",
    techDocS5I4: "Historikk og verktøydokumentasjon for datamigrering",
    techDocS6Title: "Sikkerhet & Tilgangskontroll",
    techDocS6Desc: "Retningslinjer og kontroller som beskytter systemet mot uautorisert tilgang og angrep.",
    techDocS6I1: "Dokumentasjon for identitets- og tilgangsadministrasjon (IAM)",
    techDocS6I2: "Tilnærming til hemmelighetsadministrasjon (hvelv, nøkkelrotasjonsretningslinjer)",
    techDocS6I3: "Nettverkssikkerhetsdokumentasjon (brannmurer, VPN-er, segmentering)",
    techDocS6I4: "Dokumentasjon av sikkerhetspolicyer og prosedyrer",
    techDocS7Title: "Samsvar & Sertifiseringer",
    techDocS7Desc: "Formelle sertifiseringer og revisjonsrapporter som demonstrerer regulatorisk og sikkerhetsmessig samsvar.",
    techDocS7I1: "Sikkerhetssertifiseringer (ISO 27001, SOC 2 Type II, PCI-DSS)",
    techDocS7I2: "Dokumentasjon for personvernoverholdelse (GDPR, CCPA, PIPEDA)",
    techDocS7I3: "Penetrasjonstestrapporter fra de siste 12–24 månedene",
    techDocS7I4: "Historikk for sårbarhetshåndtering og patchkadanse",
    techDocS8Title: "API & Tredjepartsintegrasjoner",
    techDocS8Desc: "Dokumentasjon av hvordan systemet eksponerer data og kobler til eksterne tjenester.",
    techDocS8I1: "API-spesifikasjoner og dokumentasjon (OpenAPI/Swagger)",
    techDocS8I2: "Inventar over tredjepartsintegrasjoner med leverandørers SLA-er",
    techDocS8I3: "Dokumentasjon for autentisering og autorisasjon (OAuth, SSO, API-nøkler)",
    techDocS8I4: "Dokumentasjon for webhooks og hendelsesdrevne integrasjoner",
    techDocS9Title: "Katastrofegjenoppretting & Forretningskontinuitet",
    techDocS9Desc: "Planer og bevis på at systemet kan komme seg etter feil og fortsette å fungere.",
    techDocS9I1: "Plan for katastrofegjenoppretting (DR) og forretningskontinuitetsplan (BCP)",
    techDocS9I2: "RTO/RPO-mål og historiske testresultater",
    techDocS9I3: "Hendelseshåndteringsprosedyrer og eskaleringsdokumentasjon",
    techDocS9I4: "Hendelsesrapporter fra de siste 24 månedene",
    techDocS10Title: "Teknisk gjeld & Veikart",
    techDocS10Desc: "Transparens om kjente begrensninger, fremtidige planer og forpliktelser for åpen kildekode.",
    techDocS10I1: "Inventar og prioritering av kjent teknisk gjeld",
    techDocS10I2: "Liste over utdaterte eller avviklede komponenter og biblioteker",
    techDocS10I3: "Produkt- og teknisk veikart (12–24 måneder)",
    techDocS10I4: "Forpliktelser og overholdelse av åpen kildekode-lisenser",
    techDocS11Title: "Finansielle rapporter & Revisjon",
    techDocS11Desc: "Reviderte finansielle rapporter og uavhengige granskingsrapporter som dekker de siste tre til fem regnskapsårene.",
    techDocS11I1: "Reviderte årsregnskap (resultatregnskap, balanse, kontantstrøm) for de siste 3–5 årene",
    techDocS11I2: "Revisjonsberetninger og management letters fra uavhengig revisor",
    techDocS11I3: "Delregnskap og interne ledelsesrapporter (innevværende år)",
    techDocS11I4: "Regnskapsprinsipper samt eventuelle omregninger eller justeringer av tidligere år",
    techDocS12Title: "Inntekter & Forretningsmodell",
    techDocS12Desc: "Detaljert oversikt over inntektsstrømmer, kundekonsentrasjon og baerekraften til forretningsmodellen.",
    techDocS12I1: "Inntektsfordeling per produkt, tjenestelinje, geografi og kundesegment",
    techDocS12I2: "Topp-10-kundeliste med avtalebetingelser, fornyelsesdatoer og churn-data",
    techDocS12I3: "Analyse av tilbakevendende kontra engangsinntekter samt kohortbasert retensjonsdata",
    techDocS12I4: "Dokumentasjon av prismodell og historiske prisendringer",
    techDocS13Title: "Arbeidskapital & Kontantstrøm",
    techDocS13Desc: "Analyse av selskapets likviditet, kontantstrømgenerering og eksisterende gjeldsforpliktelser.",
    techDocS13I1: "Arbeidskapitalanalyse (aldersanalyse av fordringer/gjeld, lagernivåer, dager utestende)",
    techDocS13I2: "Historiske og prognoserte kontantstrømanalyser med sentrale forutsetninger",
    techDocS13I3: "Gjeldsplan, kredittfasiliteter, covenants og eventuelle pantsatte eiendeler",
    techDocS13I4: "Historikk for investeringsutgifter (CapEx) samt oppdeling i vedlikeholds- og vekstkepex",
    techDocS14Title: "Skatte- & Juridiske Forpliktelser",
    techDocS14Desc: "Oversikt over skatteoverensstemmelse, utestende forpliktelser og eventuelle vesentlige juridiske eller regulatoriske eksponeringer.",
    techDocS14I1: "Innleverte selvangivelser for de siste 3–5 årene (selskapsskatt, mva)",
    techDocS14I2: "Pågående skattetvister, skattevedtak eller åpne revisjonssaker",
    techDocS14I3: "Oversikt over betingede forpliktelser, avsetninger og poster utenfor balansen",
    techDocS14I4: "Vesentlige kontrakter, pågående rettssaker og regulatorisk korrespondanse",
  },

  da: {
    // Common
    search: "Søg...",
    signOut: "Log ud",
    admin: "Admin",
    user: "Bruger",
    save: "Gem",
    cancel: "Annuller",
    create: "Opret",
    edit: "Rediger",
    delete: "Slet",
    filter: "Filtrer",
    actions: "Handlinger",
    noResults: "Ingen resultater fundet",

    // Project Detail
    projectDetails: "Projektdetaljer",
    projectNotFound: "Projekt ikke fundet",
    unlockEdit: "Lås op & Rediger",
    projectSaved: "Projekt Gemt",
    projectSavedDesc: "Projektet er blevet opdateret.",
    documents: "Dokumenter",
    uploadDocuments: "Upload Dokumenter",
    documentsUploaded: "Dokumenter Uploadet",
    documentsUploadedDesc: "dokumenter er blevet uploadet.",
    noDocuments: "Ingen dokumenter uploadet endnu.",

    // Navigation
    dashboard: "Oversigt",
    project: "Projekt",
    projects: "Projekter",
    tasks: "Opgaver/Handlinger",
    reports: "Rapporter & Indsigt",
    usersRoles: "Brugere & Roller",

    // Dashboard
    dashboardTitle: "Oversigt",
    dashboardSubtitle: "Følg din due diligence-pipeline og fremskridt",
    totalProjects: "Samlede Projekter",
    activeProjects: "Aktive Projekter",
    totalDealValue: "Samlet Projektværdi",
    avgDDProgress: "Gennemsnit DD-fremskridt",
    fromLastMonth: "fra sidste måned",
    projectReports: "Projektrapporter",
    categoryBreakdown: "Kategorifordeling",

    // Project / Due Diligence
    dueDiligence: "Due Diligence",
    addProject: "Tilføj Projekt",
    projectTarget: "Målnavn",
    status: "Status",
    phase: "Fase",
    dealValue: "Projektværdi",
    ddProgress: "Fremskridt",
    risk: "Risiko",
    redFlags: "Røde flag",
    redacted: "Redigeret indhold",
    showRedacted: "Vis redigeret indhold",
    hideRedacted: "Skjul redigeret indhold",
    redactedCommaSeparated: "Adskil flere nøgleord med kommaer",
    expectedCloseDate: "Forventet slutdato",
    owner: "Ejer",
    openProject: "Åbn Projekter",
    noProjectsFound: "Ingen projekter fundet der matcher filtrene.",

    // Status
    active: "Aktiv",
    onHold: "På Pause",
    cancelled: "Annulleret",
    done: "Færdig",

    // Risk
    low: "Lav",
    medium: "Mellem",
    high: "Høj",

    // Phases
    initialReview: "Indledende Gennemgang",
    finalReview: "Slutgennemgang",
    negotiation: "Forhandling",
    closing: "Godkendelse",

    // Add Project Form
    createNewProject: "Opret Nyt Projekt",
    fillDetails: "Udfyld detaljerne nedenfor for at oprette et nyt due diligence-projekt.",
    projectTargetName: "Navn",
    enterProjectName: "Navn",
    selectStatus: "Vælg status",
    selectPhase: "Vælg fase",
    enterDealValue: "Indtast opkøbsværdi",
    selectRiskLevel: "Vælg risikoniveau",
    projectSector: "Sektor",
    selectSector: "Vælg sektor",
    projectDealType: "Aftaletype",
    selectDealType: "Vælg aftaletype",
    dealTypeAcquisition: "Opkøb",
    dealTypeMerger: "Fusion",
    dealTypeGrowthInvestment: "Vækstinvestering",
    dealTypePEBuyout: "PE-opkøb",
    dealTypeCarveOut: "Carve-out",
    dealTypeJointVenture: "Joint venture",
    dealTypeAssetPurchase: "Aktivkøb",
    numberOfRedFlags: "Antal redigeringsnøgleord",
    selectOwner: "Vælg ejer",
    createProject: "Opret Projekt",
    projectCreated: "Projekt Oprettet",
    projectCreatedDesc: "Projektet er blevet oprettet.",
    projectDeleted: "Projekt Slettet",
    projectDeletedDesc: "Projektet er blevet slettet.",
    confirmDeleteProject: "Slet Projekt",
    confirmDeleteProjectDesc: "Er du sikker på, at du vil slette",
    deleteHasCreditsError: "Kan ikke slette et projekt, der har en positiv kreditsaldo. Overfør eller brug kreditterne først.",
    projectUpdated: "Projekt Opdateret",
    projectUpdatedDesc: "Projektet er blevet opdateret.",

    // Users & Roles
    usersRolesTitle: "Brugere & Rolleadministration",
    firstName: "Fornavn",
    lastName: "Efternavn",
    email: "E-mail",
    role: "Rolle",
    department: "Afdeling",
    raci: "RACI",
    bm: "BM",
    responsible: "Ansvarlig",
    accountable: "Beslutningstager",
    consulted: "Konsulteret",
    informed: "Informeret",
    allRoles: "Alle Roller",
    allDepartments: "Alle Afdelinger",
    searchUsers: "Søg brugere...",
    noUsersFound: "Ingen brugere fundet der matcher kriterierne.",

    // Auth
    welcomeBack: "Velkommen Tilbage!",
    signInCredentials: "Indtast dine loginoplysninger",
    createAccount: "Opret Konto",
    createAccountSubtitle: "Opret din konto for at komme i gang",
    signIn: "Log Ind",
    signUp: "Registrer",
    emailAddress: "E-mailadresse",
    password: "Adgangskode",
    confirmPassword: "Bekræft Adgangskode",
    fullName: "Fulde Navn",
    rememberMe: "Husk mig",
    forgotPassword: "Glemt adgangskode?",
    noAccount: "Har du ikke en konto?",
    haveAccount: "Har du allerede en konto?",
    orContinueWith: "Eller",
    agreeTerms: "Jeg accepterer",
    termsConditions: "Vilkår og Betingelser",
    signingIn: "Logger ind...",
    creatingAccount: "Opretter konto...",
    continueWithGoogle: "Fortsæt med Google",
    continueWithMicrosoft: "Fortsæt med Microsoft",
    continueWithApple: "Fortsæt med Apple",
    supabaseRequired: "Forbind Supabase for at aktivere OAuth-login",

    // Welcome
    welcomeToVeritas: "Velkommen til Perseon Labs - Veritas",
    welcomeDescription: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",

    // New keys
    progress: "Fremskridt",
    findings: "Fund",
    tabOverview: "Oversigt",
    tabWorkstreams: "Arbejdsstrømme",
    tabTasks: "Opgaver",
    tabDocuments: "Dokumenter",
    tabQA: "Spørgsmål & Svar",
    tabRisks: "Risici/Fund",
    tabApprovals: "Godkendelser",
    projectTimeline: "Projekttidslinje",
    workstream: "Arbejdsstrøm",
    allOwners: "Alle ejere",
    wsIT: "IT",
    wsCommercial: "Kommerciel",
    wsOperations: "Drift",
    wsCyber: "Cybersikkerhed",
    wsIP: "IP",
    wsNotStarted: "Ikke Startet",
    wsInProgress: "I Gang",
    wsCompleted: "Afsluttet",
    wsBlocked: "Blokeret",
    agentCybersecurity: "Cybersikkerhed",
    agentInfrastructureCloud: "Infrastruktur & Cloud",
    agentSoftwareEngineering: "Softwareudvikling",
    agentDataAI: "Data & AI",
    agentCompliance: "Compliance",
    agentVendorThirdParty: "Leverandør & Tredjepart",
    agentIpLicensing: "IP & Licensering",
    agentArchitectureScalability: "Arkitektur & Skalerbarhed",
    agentIncidentOps: "Hændelse & Drift",
    agentPeopleOrg: "Personale & Organisation",
    agentFinancialDueDiligence: "Finansiel due diligence",
    agentSummarization: "Opsummering",
    agentContradictionDetector: "Modsigelsesdetektor",
    agentRedFlagClassifier: "Rødt flag-klassificering",
    agentGapDetector: "Gapdetektor",
    agentCrossDocSynthesis: "Dokumentsyntese",
    wsPending: "Afventende",
    wsRunning: "Kører",
    wsFailed: "Fejlet",
    riskScore: "Risikoscore",
    confidence: "Konfidens",
    keyGaps: "Vigtige Mangler",
    severity: "Alvorlighed",
    documentType: "Dokumenttype",
    recommendation: "Anbefaling",
    evidence: "Bevis",
    noWorkstreams: "Ingen arbejdsstrømme fundet for dette projekt",
    noFindings: "Ingen fund tilgængelige",
    crossAnalysisFindings: "Krydsanalyseresultater",
    summary: "Resumé",
    taskKanban: "Opgavetavle",
    addTask: "Tilføj Opgave",
    taskTodo: "At Gøre",
    taskInProgress: "I Gang",
    taskReview: "Gennemgang",
    taskDone: "Færdig",
    searchDocuments: "Søg dokumenter...",
    allTags: "Alle Tags",
    allStatuses: "Alle Statusser",
    documentName: "Dokumentnavn",
    tag: "Tag",
    version: "Version",
    reviewStatus: "Gennemgangsstatus",
    uploadedBy: "Uploadet af",
    uploadedAt: "Uploadet",
    docPending: "Afventer",
    docInReview: "Under Gennemgang",
    docApproved: "Godkendt",
    docRejected: "Afvist",
    documentDeleted: "Dokument Slettet",
    documentDeletedDesc: "Dokumentet er blevet slettet.",
    totalQuestions: "Samlede Spørgsmål",
    qaOpen: "Åben",
    qaPending: "Afventer",
    qaAnswered: "Besvaret",
    qaClosed: "Lukket",
    allWorkstreams: "Alle Arbejdsstrømme",
    addQuestion: "Tilføj Spørgsmål",
    askedOn: "Spurgt",
    answer: "Svar",
    enterAnswer: "Indtast dit svar...",
    submitAnswer: "Indsend Svar",
    totalRisks: "Samlede Risici",
    riskCritical: "Kritisk",
    riskHigh: "Høj Risiko",
    riskMedium: "Middel Risiko",
    riskLow: "Lav Risiko",
    riskInfo: "Info",
    riskHeatmap: "Risikovarmekort",
    riskRegister: "Risikoregister",
    addRisk: "Tilføj Risiko",
    riskTitle: "Risiko",
    score: "Score",
    likelihood: "Sandsynlighed",
    impact: "Påvirkning",
    riskIdentified: "Identificeret",
    riskMitigating: "Reduceres",
    riskMitigated: "Reduceret",
    riskAccepted: "Accepteret",
    approvalGates: "Godkendelsesporte",
    gateDetails: "Portdetaljer",
    decidedBy: "Besluttet af",
    conditions: "Betingelser",
    pending: "Afventer",
    go: "Godkend",
    noGo: "Afvis",
    conditional: "Betinget",
    auditTrail: "Revisionslog",
    activityLog: "Aktivitetslog",
    justNow: "Lige nu",
    ago: "siden",

    // Team & Roles Tab
    tabTeamRoles: "Team & Roller",
    tabFolders: "Mapper",
    tabPayments: "Transaktioner",

    // Payments
    buyCredits: "Køb kreditter",
    creditPackages: "Kreditpakker",
    selectPackage: "Vælg en pakke",
    credits: "kreditter",
    paymentHistory: "Transaktionshistorik",
    paymentDate: "Dato",
    paymentStatus: "Status",
    paymentAmount: "Beløb",
    paymentCredits: "Kreditter",
    paymentPending: "Afventer",
    paymentCompleted: "Gennemført",
    paymentFailed: "Mislykket",
    paymentRefunded: "Refunderet",
    paymentCancelled: "Annulleret",
    noPayments: "Ingen transaktioner endnu",
    checkoutSuccess: "Betalingen lykkedes! Kreditter er tilføjet dit projekt.",
    checkoutCancel: "Betalingen blev annulleret.",
    transferCredits: "Overfør kreditter",
    transferCreditsTitle: "Overfør kreditter",
    transferCreditsDescription: "Flyt kreditter fra denne organisation til en anden organisation, du ejer.",
    transferTargetProject: "Målorganisation",
    transferSelectTarget: "Vælg en organisation",
    transferAmount: "Beløb",
    transferSend: "Send",
    transferSuccess: "Kreditterne blev overført.",
    transferError: "Kunne ikke overføre kreditter.",
    transferInsufficientCredits: "Ikke nok kreditter til rådighed.",
    transferNoTargets: "Du ejer ingen andre organisationer at overføre kreditter til.",

    // Seats
    seats: "Pladser",
    seatsActive: "Aktive",
    seatsAvailable: "Tilgængelige",
    buySeats: "Køb pladser",
    seatPlans: "Pladspakker",
    selectSeatPlan: "Vælg en pakke",
    seatPlanBonusCredits: "kreditter bonus",
    paymentType: "Type",
    paymentTypeCredits: "Kreditter",
    paymentTypeSeats: "Pladser",
    seatStatus: "Plads",
    seatActive: "Aktiv",
    seatInactive: "Inaktiv",
    activateSeat: "Aktiver plads",
    deactivateSeat: "Deaktiver plads",
    noSeatsAvailableTooltip: "Ingen ledige pladser i puljen",
    seatEntitlementBlockedMessage: "Din organisation har brug for flere aktive pladser for at gøre dette. Bed en ejer eller redaktør om at købe eller aktivere pladser.",

    folders: "Mapper",
    newFolder: "Ny mappe",
    newSubfolder: "Ny undermappe",
    folderName: "Mappenavn",
    createFolder: "Opret mappe",
    renameFolder: "Omdøb mappe",
    deleteFolder: "Slet mappe",
    deleteFolderConfirm: "Er du sikker på, at du vil slette denne mappe? Denne handling kan ikke fortrydes.",
    folderHasChildren: "Kan ikke slette en mappe, der indeholder undermapper. Slet undermapperne først.",
    noFolders: "Ingen mapper endnu. Opret en for at komme i gang.",
    uploadFile: "Upload fil",
    fileTooLarge: "Filen overskrider grænsen på 50 MB.",
    fileTypeNotAllowed: "Filtypen er ikke tilladt. Kun dokumenter, tekstfiler og kildekodefiler er tilladt.",
    deleteFile: "Slet fil",
    deleteFileConfirm: "Er du sikker på, at du vil slette denne fil? Denne handling kan ikke fortrydes.",
    teamRoles: "Team & Roller",
    totalMembers: "Samlede Medlemmer",
    addTeamMember: "Tilføj Teammedlem",
    addTeamMemberDesc: "Søg og tilføj en bruger til projektteamet.",
    importFromDirectory: "Importer fra Katalog",
    importFromDirectoryDesc: "Vælg flere brugere fra den globale katalog for at tilføje til projektet.",
    searchTeamMembers: "Søg teammedlemmer...",
    projectRole: "Projektrolle",
    projectDepartment: "Projektafd",
    permission: "Tilladelse",
    allPermissions: "Alle Tilladelser",
    allRaci: "Alle RACI",
    projectAdmin: "Projektadmin",
    projectMember: "Medlem",
    projectViewer: "Læser",
    memberAdded: "Medlem Tilføjet",
    memberAddedDesc: "Brugeren er blevet tilføjet til projektteamet.",
    memberRemoved: "Medlem Fjernet",
    memberRemovedDesc: "Brugeren er blevet fjernet fra projektteamet.",
    membersImported: "Medlemmer Importeret",
    membersImportedDesc: "brugere er blevet tilføjet til projektteamet.",
    resetToGlobal: "Nulstil til Global",
    resetToGlobalDesc: "Projektoverides er blevet nulstillet til globale standardværdier.",
    removeMember: "Fjern Medlem",
    noMembersFound: "Ingen teammedlemmer fundet.",
    noUsersAvailable: "Ingen brugere tilgængelige at tilføje.",
    allUsersAdded: "Alle brugere er blevet tilføjet til projektet.",
    roleOwner: "Ejer",
    roleEditor: "Redaktør",
    addUserByEmail: "Tilføj Bruger via E-mail",
    enterUserEmail: "Indtast brugerens e-mailadresse...",
    roleChanged: "Rolle Opdateret",
    roleChangedDesc: "Brugerens rolle er blevet ændret.",
    confirmRemoveUser: "Fjern bruger fra projektet?",
    confirmRemoveUserDesc: "Dette vil tilbagekalde brugerens adgang til projektet. Handlingen kan ikke fortrydes.",
    usersSelected: "brugere valgt",
    manageFolderUsers: "Administrer mappebrugere",
    addFolderMember: "Tilføj medlem",
    selectProjectUser: "Vælg et projektmedlem...",
    noFolderMembers: "Ingen brugere er tildelt denne mappe.",
    confirmRemoveFolderUser: "Fjern bruger fra mappen?",
    confirmRemoveFolderUserDesc: "Dette vil tilbagekalde brugerens adgang på mappeniveau. Handlingen kan ikke fortrydes.",
    importUsers: "Importer Valgte",
    global: "Global",
    name: "Navn",
    addToProject: "Tilføj til Projekt",
    selectProject: "Vælg Projekt",
    useGlobalDefaults: "Brug globale standardværdier",

    // Dashboard Customization
    customize: "Tilpas",
    customizeDashboard: "Tilpas Dashboard",
    customizeDashboardDesc: "Vælg hvilke widgets der skal vises på dit dashboard",
    enableAll: "Aktiver Alle",
    disableAll: "Deaktiver Alle",
    resetToDefault: "Nulstil til Standard",
    customizationDone: "Færdig",

    // Reports & Insights
    reportsSubtitle: "Rapporter, indsigt og analyser",
    exportBtn: "Eksporter",
    exportSections: "Sektioner der skal eksporteres",
    selectAll: "Vælg alle",
    riskCompliance: "Risiko & Compliance",
    portfolioAnalysis: "Porteføljeanalyse",
    complianceAudit: "Compliance & Revision",
    customReports: "Brugerdefinerede Rapporter",
    riskHeatmapTitle: "Risiko Heatmap",
    riskDistribution: "Risikofordeling per kategori",
    riskTrends: "Risikotendenser",
    increasingDecreasing: "Stigende/faldende risiko over tid",
    policyDeviations: "Policyafvigelser",
    deviationsFromPolicy: "Afvigelser fra policy og tærskelværdier",
    projectLabel: "Projekt",
    policyLabel: "Policy",
    deviationLabel: "Afvigelse",
    severityLabel: "Alvorlighed",
    lastMonth: "Sidste måned",
    thisMonth: "Denne måned",
    trend: "Tendens",
    it: "IT",
    critical: "Kritisk",
    projectVsProject: "Projekt vs Projekt",
    compareProjects: "Sammenlign projekter baseret på risiko og konfidens",
    confidenceScore: "Konfidenspoint",
    benchmarkScore: "Benchmarkscore",
    riskVsConfidence: "Risiko vs Konfidensmatrix",
    projectPositioning: "Positionering af projekter",
    vsLastQuarter: "vs sidste kvartal",
    youAreBetter: "I ligger bedre",
    checklistCompletion: "Tjekliste Fuldførelsesrate",
    completionByCategory: "Fuldførelse af tjeklister per kategori",
    decisionHistory: "Beslutningshistorik",
    whoApprovedWhat: "Hvem godkendte hvad – hvornår",
    fullAuditTrail: "Fuld Revisionslog",
    completeTraceability: "Komplet sporbarhed for alle aktiviteter",
    timestamp: "Tidspunkt",
    userLabel: "Bruger",
    actionLabel: "Handling",
    objectLabel: "Objekt",
    approved: "Godkendt",
    rejected: "Afvist",
    submitted: "Indsendt",
    updated: "Opdateret",
    savedTemplates: "Gemte Skabeloner",
    yourTemplates: "Dine rapportskabeloner",
    lastUsed: "Sidst brugt",
    newTemplate: "Ny Skabelon",
    buildOwnReport: "Byg Din Egen Rapport",
    selectKpis: "Vælg KPIer",
    selectChartType: "Vælg Diagramtype",
    barChart: "Søjle",
    lineChart: "Linje",
    pieChart: "Cirkel",
    heatmapChart: "Heatmap",
    dataSources: "Datakilder",
    allProjects: "Alle projekter",
    activeProjectsFilter: "Aktive projekter",
    completedProjects: "Afsluttede projekter",
    last30Days: "Sidste 30 dage",
    lastQuarter: "Sidste kvartal",
    lastYear: "Sidste år",
    preview: "Forhåndsvis",
    saveAsTemplate: "Gem som Skabelon",
    completionRate: "Fuldførelsesrate",
    riskCount: "Antal risici",
    approvals: "Godkendelser",
    review: "Gennemgang",

    // Settings
    settings: "Indstillinger",
    settingsTitle: "Brugerindstillinger",
    settingsSubtitle: "Administrer din profil, udseende og sprogindstillinger",
    profileSettings: "Profil",
    displayName: "Visningsnavn",
    displayNamePlaceholder: "Indtast dit visningsnavn",
    themeSettings: "Udseende",
    themeLight: "Lys",
    themeDark: "Mørk",
    themeSystem: "System",
    languageSettings: "Sprog",
    settingsSaved: "Indstillinger gemt",
    settingsSavedDesc: "Dine indstillinger er opdateret.",
    settingsError: "Kunne ikke gemme indstillinger. Prøv igen.",

    // Organizations
    organization: "Organisation",
    organizations: "Organisationer",
    organizationsSubtitle: "Administrer organisationer, medlemmer, projekter og kreditter.",
    addOrganization: "Tilføj organisation",
    createNewOrganization: "Opret ny organisation",
    fillOrganizationDetails: "Udfyld organisationsdetaljer.",
    organizationName: "Organisationsnavn",
    description: "Beskrivelse",
    organizationNumber: "Organisationsnummer",
    contactEmail: "Kontakt-e-mail",
    country: "Land",
    organizationOwner: "Organisationsejer",
    createOrganization: "Opret organisation",
    organizationNotFound: "Organisation ikke fundet",
    organizationCreditBalance: "Organisationens kreditsaldo",
    organizationProjects: "Projekter",
    organizationUsers: "Brugere",
    organizationDetails: "Organisationsdetaljer",
    lastUpdated: "Sidst opdateret",
    createdAt: "Oprettet",
    viewProjects: "Vis projekter",
    searchOrganizations: "Søg organisationer...",
    openOrganization: "Åbn organisation",
    noOrganizationsFound: "Ingen organisationer fundet",
    confirmDeleteOrganization: "Slet organisation?",
    confirmDeleteOrganizationDesc: "Dette sletter permanent",
    organizationDeleteBlocked: "Organisationer kan kun slettes, når kreditsaldoen er 0, og de ikke har projekter.",
    selectOrganization: "Vælg organisation",
    createOrganizationFirst: "Opret en organisation, før du tilføjer et projekt.",
    addOrganizationMemberDesc: "Tilføj et organisationsmedlem via e-mail.",
    inviteMember: "Inviter",
    inviteMemberDesc: "Inviter en bruger til denne organisation via e-mail. De skal acceptere invitationen, før de får adgang til organisationens data.",
    pendingInvitationBadge: "Afventer",
    pendingInvitationsTitle: "Afventende invitationer",
    acceptInvitation: "Accepter",
    declineInvitation: "Afvis",
    invitedToJoin: "inviterede dig til",
    organizationMembersLoadError: "Kunne ikke indlæse organisationsmedlemmer.",
    confirmRemoveOrganizationUserDesc: "Fjern denne bruger fra organisationen?",
    addExistingProject: "Tilføj eksisterende projekt",
    moveProjectWarning: "Flytning af et projekt ændrer hvilken organisation der ejer kreditterne og rettighedskonteksten.",
    moveProject: "Flyt projekt",
    searchProjectsPlaceholder: "Søg efter projekter...",
    noMatchingProjects: "Ingen matchende projekter fundet.",
    searchToFindProjects: "Søg for at finde et projekt, du kan tilføje til denne organisation.",
    transferTargetOrganization: "Målorganisation",
    previous: "Forrige",
    next: "Næste",

    // Report Generation
    generateReport: "Kør agenter",
    generatingReport: "Kører...",
    reportGenerated: "AI-arbejdsgange fuldført",
    confirmGenerateReport: "Bekræft kørsel af agenter",
    confirmGenerateReportDesc: "Er du sikker på, at du vil køre AI-arbejdsgange for dette projekt? Alle uploadede filer vil blive behandlet.",
    projectCreditBalance: "Kreditsaldo",
    creditsUsableUntil: "Kreditter kan bruges til",
    creditsUsableUntilUnavailable: "Manglende dato for kreditudløb",
    requiredCredits: "Minimum påkrævede kreditter",
    insufficientProjectCredits: "Utilstrækkelige kreditter til denne kørsel.",

    // Technical Documentation Guide
    technicalDocumentation: "Guide",
    techDocTitle: "Due Diligence-guide",
    techDocSubtitle: "En omfattende tjekliste over dokumentation, som en køber bør kræve af en sælger ved due diligence.",
    techDocIntro: "Brug denne guide til at sikre, at du har modtaget og gennemgået al kritisk dokumentation fra målvirksomheden, inden du fuldfører dit opkøb.",
    techDocRequired: "Påkrævet",
    techDocRecommended: "Anbefalet",
    techDocS1Title: "Systemarkitektur",
    techDocS1Desc: "Dokumentation, der beskriver, hvordan systemet er struktureret, hvordan komponenter interagerer, og hvilke teknologier der anvendes.",
    techDocS1I1: "Overordnede arkitekturdiagrammer (monolit, mikrotjenester, serverløst)",
    techDocS1I2: "Komponent- og tjenesteinteraktionsdiagrammer",
    techDocS1I3: "Dokumentation af teknologistak (sprog, frameworks, databaser)",
    techDocS1I4: "Dataflow- og integrationsdiagrammer",
    techDocS2Title: "Kildekode & Versionsstyring",
    techDocS2Desc: "Bevis for, hvordan kildekode administreres, gennemgås og vedligeholdes.",
    techDocS2I1: "Adgang til kildekoderepositories (GitHub, GitLab, Bitbucket)",
    techDocS2I2: "Forgreningsstrategi og dokumentation af releaseprocessen",
    techDocS2I3: "Retningslinjer for kodegennemgang og kvalitetsstandarder",
    techDocS2I4: "Opgørelse over tredjepartsbiblioteker og afhængigheder med licenser",
    techDocS3Title: "Infrastruktur & Cloud",
    techDocS3Desc: "Dokumentation, der dækker hostingmiljøer, cloudopsætning og netværksarkitektur.",
    techDocS3I1: "Hosting- og cloudinfrastrukturdokumentation (AWS, Azure, GCP)",
    techDocS3I2: "Infrastruktur-som-kode-scripts (Terraform, CloudFormation, Pulumi)",
    techDocS3I3: "Netværkstopologi og arkitekturdiagrammer",
    techDocS3I4: "Skaleringsarkitektur og konfiguration af belastningsfordeling",
    techDocS4Title: "CI/CD & DevOps",
    techDocS4Desc: "Processer og værktøjer, der bruges til pålideligt at bygge, teste og implementere software.",
    techDocS4I1: "CI/CD-pipeline-dokumentation og -værktøjer (Jenkins, GitHub Actions, m.fl.)",
    techDocS4I2: "Implementeringsprocedurer og tilbagerulningsplaner",
    techDocS4I3: "Miljøkonfiguration (udvikling, staging, produktion)",
    techDocS4I4: "Releasekadence, versionspolitik og ændringsloghistorik",
    techDocS5Title: "Database & Datahåndtering",
    techDocS5Desc: "Dokumentation, der beviser, at data er velstrukturerede, sikkerhedskopierede og gendannelige.",
    techDocS5I1: "Databaseskema og entitets-relationsdiagrammer (ER)",
    techDocS5I2: "Politikker for dataopbevaring og -arkivering",
    techDocS5I3: "Procedurer for sikkerhedskopiering og gendannelse med testresultater",
    techDocS5I4: "Historik og dokumentation af værktøjer til datamigrering",
    techDocS6Title: "Sikkerhed & Adgangskontrol",
    techDocS6Desc: "Politikker og kontroller, der beskytter systemet mod uautoriseret adgang og angreb.",
    techDocS6I1: "Dokumentation for identitets- og adgangsstyring (IAM)",
    techDocS6I2: "Tilgang til hemmelighedsstyring (hvælvinger, nøglerotationspolitikker)",
    techDocS6I3: "Netværkssikkerhedsdokumentation (firewalls, VPN'er, segmentering)",
    techDocS6I4: "Dokumentation af sikkerhedspolitikker og procedurer",
    techDocS7Title: "Overholdelse & Certificeringer",
    techDocS7Desc: "Formelle certificeringer og revisionsrapporter, der dokumenterer regulatorisk og sikkerhedsmæssig overholdelse.",
    techDocS7I1: "Sikkerhedscertificeringer (ISO 27001, SOC 2 Type II, PCI-DSS)",
    techDocS7I2: "Dokumentation for overholdelse af privatlivslovgivning (GDPR, CCPA, PIPEDA)",
    techDocS7I3: "Penetrationstestrapporter fra de seneste 12–24 måneder",
    techDocS7I4: "Historik for sårbarhedsstyring og patchkadence",
    techDocS8Title: "API & Tredjeparts integrationer",
    techDocS8Desc: "Dokumentation af, hvordan systemet eksponerer data og forbinder sig til eksterne tjenester.",
    techDocS8I1: "API-specifikationer og dokumentation (OpenAPI/Swagger)",
    techDocS8I2: "Opgørelse over tredjeparts integrationer med leverandørernes SLA'er",
    techDocS8I3: "Dokumentation for godkendelse og autorisation (OAuth, SSO, API-nøgler)",
    techDocS8I4: "Dokumentation for webhooks og hændelsesdrevne integrationer",
    techDocS9Title: "Katastrofegendannelse & Forretningskontinuitet",
    techDocS9Desc: "Planer og dokumentation for, at systemet kan komme sig efter fejl og fortsætte med at fungere.",
    techDocS9I1: "Plan for katastrofegendannelse (DR) og forretningskontinuitetsplan (BCP)",
    techDocS9I2: "RTO/RPO-mål og historiske testresultater",
    techDocS9I3: "Hændelseshåndteringsprocedurer og eskaleringsdokumentation",
    techDocS9I4: "Hændelsesrapporter fra de seneste 24 måneder",
    techDocS10Title: "Teknisk gæld & Køreplan",
    techDocS10Desc: "Gennemsigtighed om kendte begrænsninger, fremtidige planer og open source-forpligtelser.",
    techDocS10I1: "Opgørelse og prioritering af kendte teknisk gæld",
    techDocS10I2: "Liste over udgåede eller forladte komponenter og biblioteker",
    techDocS10I3: "Produkt- og teknisk køreplan (12–24 måneder)",
    techDocS10I4: "Forpligtelser og overholdelse af open source-licenser",
    techDocS11Title: "Finansielle rapporter & Revision",
    techDocS11Desc: "Reviderede årsregnskaber og uafhængige revisionsrapporter, der dækker de seneste tre til fem regnskabsår.",
    techDocS11I1: "Reviderede årsregnskaber (resultatopgørelse, balance, pengestrømsopgørelse) for de seneste 3–5 år",
    techDocS11I2: "Revisionsberetninger og management letters fra uafhængig revisor",
    techDocS11I3: "Perioderegnskaber og interne ledelsesrapporter (indeværende år)",
    techDocS11I4: "Regnskabsprincipper samt eventuelle genfremlæggelser eller justeringer af tidligere år",
    techDocS12Title: "Omsaetning & Forretningsmodel",
    techDocS12Desc: "Detaljeret opdeling af indtægtsstrømme, kundekoncentration og bæredygtigheden af forretningsmodellen.",
    techDocS12I1: "Indtægtsfordeling pr. produkt, servicelinje, geografi og kundesegment",
    techDocS12I2: "Top-10-kundeliste med kontraktbetingelser, fornyelsesdatoer og churn-data",
    techDocS12I3: "Analyse af tilbagevendende versus engangsindtaegter samt kohortbaseret fastholdelsesdata",
    techDocS12I4: "Dokumentation af prismodel og historiske prisaendringer",
    techDocS13Title: "Driftskapital & Pengestrøm",
    techDocS13Desc: "Analyse af selskabets likviditet, pengestrømsgenerering og eksisterende gældsforpligtelser.",
    techDocS13I1: "Driftskapitalanalyse (aldersfordeling af tilgodehavender/leverandørgæld, lagerniveauer, dage udestående)",
    techDocS13I2: "Historiske og prognosticerede pengestrømsanalyser med centrale antagelser",
    techDocS13I3: "Gældsplan, kreditfaciliteter, covenants og eventuelle pantsatte aktiver",
    techDocS13I4: "Historik for investeringsudgifter (CapEx) samt opdeling i vedligeholdelses- og vaekst-CapEx",
    techDocS14Title: "Skatte- & Juridiske Forpligtelser",
    techDocS14Desc: "Oversigt over skatteoverholdelses, udestående forpligtelser og eventuelle væsentlige juridiske eller regulatoriske eksponeringer.",
    techDocS14I1: "Indgivne selvangivelser for de seneste 3–5 år (selskabsskat, moms)",
    techDocS14I2: "Igangværende skattesager, skatteafgørelser eller åbne revisionsspørgsmål",
    techDocS14I3: "Oversigt over betingede forpligtelser, hensaettelser og poster uden for balancen",
    techDocS14I4: "Væsentlige kontrakter, verserende retssager og regulatorisk korrespondance",
  },
  
  fi: {
    // Common
    search: "Hae...",
    signOut: "Kirjaudu ulos",
    admin: "Ylläpitäjä",
    user: "Käyttäjä",
    save: "Tallenna",
    cancel: "Peruuta",
    create: "Luo",
    edit: "Muokkaa",
    delete: "Poista",
    filter: "Suodata",
    actions: "Toiminnot",
    noResults: "Ei tuloksia",
    
    // Project Detail
    projectDetails: "Projektin Tiedot",
    projectNotFound: "Projektia ei löydy",
    unlockEdit: "Avaa & Muokkaa",
    projectSaved: "Projekti Tallennettu",
    projectSavedDesc: "Projekti on päivitetty.",
    documents: "Dokumentit",
    uploadDocuments: "Lataa Dokumentteja",
    documentsUploaded: "Dokumentit Ladattu",
    documentsUploadedDesc: "dokumenttia on ladattu.",
    noDocuments: "Ei ladattuja dokumentteja vielä.",
    
    // Navigation
    dashboard: "Hallintapaneeli",
    project: "Projekti",
    projects: "Projektit",
    tasks: "Tehtävät/Toiminnot",
    reports: "Raportit & Näkemykset",
    usersRoles: "Käyttäjät & Roolit",
    
    // Dashboard
    dashboardTitle: "Hallintapaneeli",
    dashboardSubtitle: "Seuraa due diligence -prosessiasi ja etenemistä",
    totalProjects: "Projektit Yhteensä",
    activeProjects: "Aktiiviset Projektit",
    totalDealValue: "Projektin Kokonaisarvo",
    avgDDProgress: "DD-eteneminen Keskim.",
    fromLastMonth: "viime kuusta",
    projectReports: "Projektiraportit",
    categoryBreakdown: "Kategoriajakauma",
    
    // Project / Due Diligence
    dueDiligence: "Due Diligence",
    addProject: "Lisää Projekti",
    projectTarget: "Kohteen Nimi",
    status: "Tila",
    phase: "Vaihe",
    dealValue: "Projektin Arvo",
    ddProgress: "Eteneminen",
    risk: "Riski",
    redFlags: "Punaiset liput",
    redacted: "Poistettu sisältö",
    showRedacted: "Näytä muokattu sisältö",
    hideRedacted: "Piilota muokattu sisältö",
    redactedCommaSeparated: "Erota useammat avainsanat pilkuilla",
    expectedCloseDate: "Arvioitu sulkemispäivä",
    owner: "Omistaja",
    openProject: "Avaa Projektit",
    noProjectsFound: "Suodattimia vastaavia projekteja ei löytynyt.",
    
    // Status
    active: "Aktiivinen",
    onHold: "Odottaa",
    cancelled: "Peruttu",
    done: "Valmis",
    
    // Risk
    low: "Matala",
    medium: "Keskitaso",
    high: "Korkea",
    
    // Phases
    initialReview: "Alustava Tarkastelu",
    finalReview: "Lopputarkastelu",
    negotiation: "Neuvottelu",
    closing: "Hyväksyntä",
    
    // Add Project Form
    createNewProject: "Luo Uusi Projekti",
    fillDetails: "Täytä tiedot alla luodaksesi uuden due diligence -projektin.",
    projectTargetName: "Nimi",
    enterProjectName: "Nimi",
    selectStatus: "Valitse tila",
    selectPhase: "Valitse vaihe",
    enterDealValue: "Syötä ostoarvo",
    selectRiskLevel: "Valitse riskitaso",
    projectSector: "Toimiala",
    selectSector: "Valitse toimiala",
    projectDealType: "Kauppatyyppi",
    selectDealType: "Valitse kauppatyyppi",
    dealTypeAcquisition: "Yritysosto",
    dealTypeMerger: "Sulautuminen",
    dealTypeGrowthInvestment: "Kasvuinvestointi",
    dealTypePEBuyout: "PE-osto",
    dealTypeCarveOut: "Carve-out",
    dealTypeJointVenture: "Yhteisyritys",
    dealTypeAssetPurchase: "Liiketoimintakauppa",
    numberOfRedFlags: "Muokkausavainsanojen määrä",
    selectOwner: "Valitse omistaja",
    createProject: "Luo Projekti",
    projectCreated: "Projekti Luotu",
    projectCreatedDesc: "Projekti on luotu onnistuneesti.",
    projectDeleted: "Projekti Poistettu",
    projectDeletedDesc: "Projekti on poistettu.",
    confirmDeleteProject: "Poista Projekti",
    confirmDeleteProjectDesc: "Haluatko varmasti poistaa",
    deleteHasCreditsError: "Projektia, jolla on positiivinen krediittisaldo, ei voi poistaa. Siirrä tai käytä krediitit ensin.",
    projectUpdated: "Projekti Päivitetty",
    projectUpdatedDesc: "Projekti on päivitetty.",
    
    // Users & Roles
    usersRolesTitle: "Käyttäjät & Roolihallinta",
    firstName: "Etunimi",
    lastName: "Sukunimi",
    email: "Sähköposti",
    role: "Rooli",
    department: "Osasto",
    raci: "RACI",
    bm: "BM",
    responsible: "Vastuullinen",
    accountable: "Päätöksentekijä",
    consulted: "Konsultoitu",
    informed: "Informoitu",
    allRoles: "Kaikki Roolit",
    allDepartments: "Kaikki Osastot",
    searchUsers: "Hae käyttäjiä...",
    noUsersFound: "Kriteerejä vastaavia käyttäjiä ei löytynyt.",
    
    // Auth
    welcomeBack: "Tervetuloa Takaisin!",
    signInCredentials: "Syötä kirjautumistietosi",
    createAccount: "Luo Tili",
    createAccountSubtitle: "Luo tilisi aloittaaksesi",
    signIn: "Kirjaudu",
    signUp: "Rekisteröidy",
    emailAddress: "Sähköpostiosoite",
    password: "Salasana",
    confirmPassword: "Vahvista Salasana",
    fullName: "Koko Nimi",
    rememberMe: "Muista minut",
    forgotPassword: "Unohditko salasanan?",
    noAccount: "Eikö sinulla ole tiliä?",
    haveAccount: "Onko sinulla jo tili?",
    orContinueWith: "Tai",
    agreeTerms: "Hyväksyn",
    termsConditions: "Käyttöehdot",
    signingIn: "Kirjaudutaan...",
    creatingAccount: "Luodaan tiliä...",
    continueWithGoogle: "Jatka Googlella",
    continueWithMicrosoft: "Jatka Microsoftilla",
    continueWithApple: "Jatka Applella",
    supabaseRequired: "Yhdistä Supabase ottaaksesi OAuth-kirjautumisen käyttöön",
    
    // Welcome
    welcomeToVeritas: "Tervetuloa Perseon Labs - Veritakseen",
    welcomeDescription: "Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.",
    
    // New keys
    progress: "Edistyminen",
    findings: "Löydökset",
    tabOverview: "Yleiskatsaus",
    tabWorkstreams: "Työvirrat",
    tabTasks: "Tehtävät",
    tabDocuments: "Dokumentit",
    tabQA: "Kysymykset & Vastaukset",
    tabRisks: "Riskit/Löydökset",
    tabApprovals: "Hyväksynnät",
    projectTimeline: "Projektin Aikajana",
    workstream: "Työvirta",
    allOwners: "Kaikki omistajat",
    wsIT: "IT",
    wsCommercial: "Kaupallinen",
    wsOperations: "Toiminnot",
    wsCyber: "Kyberturvallisuus",
    wsIP: "IP",
    wsNotStarted: "Ei Aloitettu",
    wsInProgress: "Käynnissä",
    wsCompleted: "Valmis",
    wsBlocked: "Estetty",
    agentCybersecurity: "Kyberturvallisuus",
    agentInfrastructureCloud: "Infrastruktuuri & Pilvi",
    agentSoftwareEngineering: "Ohjelmistokehitys",
    agentDataAI: "Data & Tekoäly",
    agentCompliance: "Vaatimustenmukaisuus",
    agentVendorThirdParty: "Toimittaja & Kolmas Osapuoli",
    agentIpLicensing: "IP & Lisensointi",
    agentArchitectureScalability: "Arkkitehtuuri & Skaalautuvuus",
    agentIncidentOps: "Poikkeamat & Toiminnot",
    agentPeopleOrg: "Henkilöstö & Organisaatio",
    agentFinancialDueDiligence: "Taloudellinen due diligence",
    agentSummarization: "Yhteenveto",
    agentContradictionDetector: "Ristiriidantunnistus",
    agentRedFlagClassifier: "Punaisen lipun luokittelu",
    agentGapDetector: "Aukkotunnistus",
    agentCrossDocSynthesis: "Dokumenttisynteesi",
    wsPending: "Odottaa",
    wsRunning: "Käynnissä",
    wsFailed: "Epäonnistunut",
    riskScore: "Riskipistemäärä",
    confidence: "Luottamus",
    keyGaps: "Keskeiset Puutteet",
    severity: "Vakavuus",
    documentType: "Dokumenttityyppi",
    recommendation: "Suositus",
    evidence: "Todiste",
    noWorkstreams: "Ei työvirta löytynyt tälle projektille",
    noFindings: "Ei löydöksiä saatavilla",
    crossAnalysisFindings: "Ristianalyysitulokset",
    summary: "Yhteenveto",
    taskKanban: "Tehtävätaulu",
    addTask: "Lisää Tehtävä",
    taskTodo: "Tehtävä",
    taskInProgress: "Käynnissä",
    taskReview: "Tarkistus",
    taskDone: "Valmis",
    searchDocuments: "Etsi dokumentteja...",
    allTags: "Kaikki Tagit",
    allStatuses: "Kaikki Tilat",
    documentName: "Dokumentin Nimi",
    tag: "Tagi",
    version: "Versio",
    reviewStatus: "Tarkistusstatus",
    uploadedBy: "Lähettäjä",
    uploadedAt: "Lähetetty",
    docPending: "Odottaa",
    docInReview: "Tarkistuksessa",
    docApproved: "Hyväksytty",
    docRejected: "Hylätty",
    documentDeleted: "Dokumentti Poistettu",
    documentDeletedDesc: "Dokumentti on poistettu.",
    totalQuestions: "Kysymyksiä Yhteensä",
    qaOpen: "Avoin",
    qaPending: "Odottaa",
    qaAnswered: "Vastattu",
    qaClosed: "Suljettu",
    allWorkstreams: "Kaikki Työvirrat",
    addQuestion: "Lisää Kysymys",
    askedOn: "Kysytty",
    answer: "Vastaus",
    enterAnswer: "Kirjoita vastauksesi...",
    submitAnswer: "Lähetä Vastaus",
    totalRisks: "Riskejä Yhteensä",
    riskCritical: "Kriittinen",
    riskHigh: "Korkea Riski",
    riskMedium: "Keskitaso Riski",
    riskLow: "Matala Riski",
    riskInfo: "Info",
    riskHeatmap: "Riskilämpökartta",
    riskRegister: "Riskirekisteri",
    addRisk: "Lisää Riski",
    riskTitle: "Riski",
    score: "Pisteet",
    likelihood: "Todennäköisyys",
    impact: "Vaikutus",
    riskIdentified: "Tunnistettu",
    riskMitigating: "Lievennetään",
    riskMitigated: "Lievennetty",
    riskAccepted: "Hyväksytty",
    approvalGates: "Hyväksyntäportit",
    gateDetails: "Portin Tiedot",
    decidedBy: "Päättänyt",
    conditions: "Ehdot",
    pending: "Odottaa",
    go: "Hyväksy",
    noGo: "Hylkää",
    conditional: "Ehdollinen",
    auditTrail: "Tarkastusloki",
    activityLog: "Aktiviteettiloki",
    justNow: "Juuri nyt",
    ago: "sitten",
    
    // Team & Roles Tab
    tabTeamRoles: "Tiimi & Roolit",
    tabFolders: "Kansiot",
    tabPayments: "Tapahtumat",

    // Payments
    buyCredits: "Osta krediittejä",
    creditPackages: "Krediittipakettit",
    selectPackage: "Valitse paketti",
    credits: "krediittiä",
    paymentHistory: "Tapahtumahistoria",
    paymentDate: "Päivämäärä",
    paymentStatus: "Tila",
    paymentAmount: "Summa",
    paymentCredits: "Krediitit",
    paymentPending: "Odottaa",
    paymentCompleted: "Valmis",
    paymentFailed: "Epäonnistunut",
    paymentRefunded: "Hyvitetty",
    paymentCancelled: "Peruttu",
    noPayments: "Ei tapahtumia vielä",
    checkoutSuccess: "Maksu onnistui! Krediitit on lisätty projektiisi.",
    checkoutCancel: "Maksu peruutettiin.",
    transferCredits: "Siirrä krediittejä",
    transferCreditsTitle: "Siirrä krediittejä",
    transferCreditsDescription: "Siirrä krediittejä tästä organisaatiosta toiseen omistamaasi organisaatioon.",
    transferTargetProject: "Kohdeorganisaatio",
    transferSelectTarget: "Valitse organisaatio",
    transferAmount: "Määrä",
    transferSend: "Lähetä",
    transferSuccess: "Krediitit siirrettiin onnistuneesti.",
    transferError: "Krediittien siirto epäonnistui.",
    transferInsufficientCredits: "Krediittejä ei ole tarpeeksi.",
    transferNoTargets: "Sinulla ei ole muita organisaatioita, joihin siirtää krediittejä.",

    // Seats
    seats: "Paikat",
    seatsActive: "Aktiiviset",
    seatsAvailable: "Käytettävissä",
    buySeats: "Osta paikkoja",
    seatPlans: "Paikkapaketit",
    selectSeatPlan: "Valitse paketti",
    seatPlanBonusCredits: "krediittiä bonus",
    paymentType: "Tyyppi",
    paymentTypeCredits: "Krediitit",
    paymentTypeSeats: "Paikat",
    seatStatus: "Paikka",
    seatActive: "Aktiivinen",
    seatInactive: "Ei aktiivinen",
    activateSeat: "Aktivoi paikka",
    deactivateSeat: "Poista paikka käytöstä",
    noSeatsAvailableTooltip: "Paikkoja ei ole saatavilla",
    seatEntitlementBlockedMessage: "Organisaatiosi tarvitsee lisää aktiivisia paikkoja tehdäkseen tämän. Pyydä omistajaa tai muokkaajaa ostamaan tai aktivoimaan paikkoja.",

    folders: "Kansiot",
    newFolder: "Uusi kansio",
    newSubfolder: "Uusi alikansio",
    folderName: "Kansion nimi",
    createFolder: "Luo kansio",
    renameFolder: "Nimeä kansio uudelleen",
    deleteFolder: "Poista kansio",
    deleteFolderConfirm: "Haluatko varmasti poistaa tämän kansion? Tätä toimintoa ei voi kumota.",
    folderHasChildren: "Kansiota, joka sisältää alikansioita, ei voi poistaa. Poista alikansiot ensin.",
    noFolders: "Ei kansioita vielä. Luo yksi aloittaaksesi.",
    uploadFile: "Lataa tiedosto",
    fileTooLarge: "Tiedosto ylittää 50 Mt:n rajan.",
    fileTypeNotAllowed: "Tiedostotyyppi ei ole sallittu. Vain asiakirjat, tekstitiedostot ja lähdekooditiedostot ovat sallittuja.",
    deleteFile: "Poista tiedosto",
    deleteFileConfirm: "Haluatko varmasti poistaa tämän tiedoston? Tätä toimintoa ei voi kumota.",
    teamRoles: "Tiimi & Roolit",
    totalMembers: "Jäseniä Yhteensä",
    addTeamMember: "Lisää Tiimijäsen",
    addTeamMemberDesc: "Etsi ja lisää käyttäjä projektitiimiin.",
    importFromDirectory: "Tuo Hakemistosta",
    importFromDirectoryDesc: "Valitse useita käyttäjiä globaalista hakemistosta lisättäväksi projektiin.",
    searchTeamMembers: "Etsi tiimijäseniä...",
    projectRole: "Projektirooli",
    projectDepartment: "Projektin Os.",
    permission: "Oikeudet",
    allPermissions: "Kaikki Oikeudet",
    allRaci: "Kaikki RACI",
    projectAdmin: "Projektin Ylläpitäjä",
    projectMember: "Jäsen",
    projectViewer: "Lukija",
    memberAdded: "Jäsen Lisätty",
    memberAddedDesc: "Käyttäjä on lisätty projektitiimiin.",
    memberRemoved: "Jäsen Poistettu",
    memberRemovedDesc: "Käyttäjä on poistettu projektitiimistä.",
    membersImported: "Jäsenet Tuotu",
    membersImportedDesc: "käyttäjää on lisätty projektitiimiin.",
    resetToGlobal: "Palauta Globaaliksi",
    resetToGlobalDesc: "Projektin ohitukset on palautettu globaaleihin oletusarvoihin.",
    removeMember: "Poista Jäsen",
    noMembersFound: "Tiimijäseniä ei löytynyt.",
    noUsersAvailable: "Ei käyttäjiä lisättäväksi.",
    allUsersAdded: "Kaikki käyttäjät on lisätty projektiin.",
    roleOwner: "Omistaja",
    roleEditor: "Muokkaaja",
    addUserByEmail: "Lisää Käyttäjä Sähköpostilla",
    enterUserEmail: "Syötä käyttäjän sähköpostiosoite...",
    roleChanged: "Rooli Päivitetty",
    roleChangedDesc: "Käyttäjän rooli on muutettu.",
    confirmRemoveUser: "Poistetaanko käyttäjä projektista?",
    confirmRemoveUserDesc: "Tämä peruuttaa käyttäjän pääsyn projektiin. Toimintoa ei voi kumota.",
    usersSelected: "käyttäjää valittu",
    manageFolderUsers: "Hallitse kansion käyttäjiä",
    addFolderMember: "Lisää jäsen",
    selectProjectUser: "Valitse projektijäsen...",
    noFolderMembers: "Tälle kansiolle ei ole määritetty käyttäjiä.",
    confirmRemoveFolderUser: "Poistetaanko käyttäjä kansiosta?",
    confirmRemoveFolderUserDesc: "Tämä peruuttaa käyttäjän kansiotason pääsyn. Toimintoa ei voi kumota.",
    importUsers: "Tuo Valitut",
    global: "Globaali",
    name: "Nimi",
    addToProject: "Lisää Projektiin",
    selectProject: "Valitse Projekti",
    useGlobalDefaults: "Käytä globaaleja oletusarvoja",
    
    // Dashboard Customization
    customize: "Mukauta",
    customizeDashboard: "Mukauta Dashboard",
    customizeDashboardDesc: "Valitse mitkä widgetit näytetään dashboardillasi",
    enableAll: "Ota Kaikki Käyttöön",
    disableAll: "Poista Kaikki Käytöstä",
    resetToDefault: "Palauta Oletukset",
    customizationDone: "Valmis",
    
    // Reports & Insights
    reportsSubtitle: "Raportit, näkemykset ja analyysit",
    exportBtn: "Vie",
    exportSections: "Vietävät osiot",
    selectAll: "Valitse kaikki",
    riskCompliance: "Riski & Compliance",
    portfolioAnalysis: "Portfolioanalyysi",
    complianceAudit: "Compliance & Tarkastus",
    customReports: "Mukautetut Raportit",
    riskHeatmapTitle: "Riskilämpökartta",
    riskDistribution: "Riskijakauma kategorioittain",
    riskTrends: "Riskitrendit",
    increasingDecreasing: "Kasvava/vähenevä riski ajan myötä",
    policyDeviations: "Linjapoikkeamat",
    deviationsFromPolicy: "Poikkeamat linjasta ja raja-arvoista",
    projectLabel: "Projekti",
    policyLabel: "Linja",
    deviationLabel: "Poikkeama",
    severityLabel: "Vakavuus",
    lastMonth: "Viime kuukausi",
    thisMonth: "Tämä kuukausi",
    trend: "Trendi",
    it: "IT",
    critical: "Kriittinen",
    projectVsProject: "Projekti vs Projekti",
    compareProjects: "Vertaa projekteja riskin ja luottamuksen perusteella",
    confidenceScore: "Luottamuspisteet",
    benchmarkScore: "Vertailupisteet",
    riskVsConfidence: "Riski vs Luottamusmatriisi",
    projectPositioning: "Projektien sijoittelu",
    vsLastQuarter: "vs viime neljännes",
    youAreBetter: "Olette parempia",
    checklistCompletion: "Tarkistuslistan Valmistumisaste",
    completionByCategory: "Tarkistuslistojen valmistuminen kategorioittain",
    decisionHistory: "Päätöshistoria",
    whoApprovedWhat: "Kuka hyväksyi mitä – milloin",
    fullAuditTrail: "Täysi Tarkastusloki",
    completeTraceability: "Täydellinen jäljitettävyys kaikille aktiviteeteille",
    timestamp: "Aikaleima",
    userLabel: "Käyttäjä",
    actionLabel: "Toiminto",
    objectLabel: "Kohde",
    approved: "Hyväksytty",
    rejected: "Hylätty",
    submitted: "Lähetetty",
    updated: "Päivitetty",
    savedTemplates: "Tallennetut Mallit",
    yourTemplates: "Raporttimallisi",
    lastUsed: "Viimeksi käytetty",
    newTemplate: "Uusi Malli",
    buildOwnReport: "Rakenna Oma Raportti",
    selectKpis: "Valitse KPIt",
    selectChartType: "Valitse Kaaviotyyppi",
    barChart: "Pylväs",
    lineChart: "Viiva",
    pieChart: "Piirakka",
    heatmapChart: "Lämpökartta",
    dataSources: "Datalähteet",
    allProjects: "Kaikki projektit",
    activeProjectsFilter: "Aktiiviset projektit",
    completedProjects: "Valmiit projektit",
    last30Days: "Viimeiset 30 päivää",
    lastQuarter: "Viime neljännes",
    lastYear: "Viime vuosi",
    preview: "Esikatselu",
    saveAsTemplate: "Tallenna Malliksi",
    completionRate: "Valmistumisaste",
    riskCount: "Riskien määrä",
    approvals: "Hyväksynnät",
    review: "Tarkistus",

    // Settings
    settings: "Asetukset",
    settingsTitle: "Käyttäjäasetukset",
    settingsSubtitle: "Hallitse profiiliasi, ulkoasua ja kieliasetuksia",
    profileSettings: "Profiili",
    displayName: "Näyttönimi",
    displayNamePlaceholder: "Kirjoita näyttönimesi",
    themeSettings: "Ulkoasu",
    themeLight: "Vaalea",
    themeDark: "Tumma",
    themeSystem: "Järjestelmä",
    languageSettings: "Kieli",
    settingsSaved: "Asetukset tallennettu",
    settingsSavedDesc: "Asetuksesi on päivitetty.",
    settingsError: "Asetusten tallennus epäonnistui. Yritä uudelleen.",

    // Organizations
    organization: "Organisaatio",
    organizations: "Organisaatiot",
    organizationsSubtitle: "Hallitse organisaatioita, jäseniä, projekteja ja krediittejä.",
    addOrganization: "Lisää organisaatio",
    createNewOrganization: "Luo uusi organisaatio",
    fillOrganizationDetails: "Täytä organisaation tiedot.",
    organizationName: "Organisaation nimi",
    description: "Kuvaus",
    organizationNumber: "Organisaatiotunnus",
    contactEmail: "Yhteyssähköposti",
    country: "Maa",
    organizationOwner: "Organisaation omistaja",
    createOrganization: "Luo organisaatio",
    organizationNotFound: "Organisaatiota ei löydy",
    organizationCreditBalance: "Organisaation krediittisaldo",
    organizationProjects: "Projektit",
    organizationUsers: "Käyttäjät",
    organizationDetails: "Organisaation tiedot",
    lastUpdated: "Viimeksi päivitetty",
    createdAt: "Luotu",
    viewProjects: "Näytä projektit",
    searchOrganizations: "Hae organisaatioita...",
    openOrganization: "Avaa organisaatio",
    noOrganizationsFound: "Organisaatioita ei löytynyt",
    confirmDeleteOrganization: "Poistetaanko organisaatio?",
    confirmDeleteOrganizationDesc: "Tämä poistaa pysyvästi",
    organizationDeleteBlocked: "Organisaation voi poistaa vain, kun krediittisaldo on 0 eikä sillä ole projekteja.",
    selectOrganization: "Valitse organisaatio",
    createOrganizationFirst: "Luo organisaatio ennen projektin lisäämistä.",
    addOrganizationMemberDesc: "Lisää organisaation jäsen sähköpostilla.",
    inviteMember: "Kutsu",
    inviteMemberDesc: "Kutsu käyttäjä tähän organisaatioon sähköpostitse. Heidän on hyväksyttävä kutsu ennen kuin he pääsevät käsiksi organisaation tietoihin.",
    pendingInvitationBadge: "Odottaa",
    pendingInvitationsTitle: "Odottavat kutsut",
    acceptInvitation: "Hyväksy",
    declineInvitation: "Hylkää",
    invitedToJoin: "kutsui sinut liittymään",
    organizationMembersLoadError: "Organisaation jäsenten lataus epäonnistui.",
    confirmRemoveOrganizationUserDesc: "Poistetaanko tämä käyttäjä organisaatiosta?",
    addExistingProject: "Lisää olemassa oleva projekti",
    moveProjectWarning: "Projektin siirtäminen muuttaa, mikä organisaatio omistaa sen krediitit ja käyttöoikeuskontekstin.",
    moveProject: "Siirrä projekti",
    searchProjectsPlaceholder: "Hae projekteja nimellä...",
    noMatchingProjects: "Vastaavia projekteja ei löytynyt.",
    searchToFindProjects: "Hae löytääksesi projektin, jonka voit lisätä tähän organisaatioon.",
    transferTargetOrganization: "Kohdeorganisaatio",
    previous: "Edellinen",
    next: "Seuraava",

    // Report Generation
    generateReport: "Suorita agentit",
    generatingReport: "Suoritetaan...",
    reportGenerated: "AI-työnkulut suoritettu",
    confirmGenerateReport: "Vahvista agenttien suorittaminen",
    confirmGenerateReportDesc: "Haluatko varmasti suorittaa AI-työnkulut tälle projektille? Kaikki ladatut tiedostot käsitellään.",
    projectCreditBalance: "Krediittisaldo",
    creditsUsableUntil: "Krediittejä voi käyttää päivään",
    creditsUsableUntilUnavailable: "Krediittien viimeinen käyttöpäivä puuttuu",
    requiredCredits: "Vaaditut vähimmäiskreditit",
    insufficientProjectCredits: "Tähän ajoon ei ole riittävästi kredittejä.",

    // Technical Documentation Guide
    technicalDocumentation: "Guide",
    techDocTitle: "Due diligence -opas",
    techDocSubtitle: "Kattava tarkistuslista dokumenteista, joita ostajan tulisi vaatia myyjältä due diligence -prosessissa.",
    techDocIntro: "Käytä tätä opasta varmistaaksesi, että olet vastaanottanut ja tarkistanut kaikki kriittiset asiakirjat kohdeyritykseltä ennen yrityskaupan viimeistelyä.",
    techDocRequired: "Vaadittu",
    techDocRecommended: "Suositeltava",
    techDocS1Title: "Järjestelmäarkkitehtuuri",
    techDocS1Desc: "Dokumentaatio, joka kuvaa järjestelmän rakennetta, komponenttien vuorovaikutusta ja käytettyjä teknologioita.",
    techDocS1I1: "Korkean tason arkkitehtuurikaaviot (monoliitti, mikropalvelut, palvelinettomuus)",
    techDocS1I2: "Komponenttien ja palveluiden vuorovaikutuskaaviot",
    techDocS1I3: "Teknologiapinon dokumentaatio (kielet, sovelluskehykset, tietokannat)",
    techDocS1I4: "Tietovirta- ja integrointikaaviot",
    techDocS2Title: "Lähdekoodi & Versionhallinta",
    techDocS2Desc: "Näyttö siitä, miten lähdekoodia hallinnoidaan, tarkistetaan ja ylläpidetään.",
    techDocS2I1: "Pääsy lähdekoodivarastoihin (GitHub, GitLab, Bitbucket)",
    techDocS2I2: "Haaroitusstrategia ja julkaisuprosessin dokumentaatio",
    techDocS2I3: "Koodikatselmoinnin käytännöt ja laatustandardit",
    techDocS2I4: "Kolmannen osapuolen kirjasto- ja riippuvuusluettelo lisensseineen",
    techDocS3Title: "Infrastruktuuri & Pilvi",
    techDocS3Desc: "Dokumentaatio, joka kattaa hosting-ympäristöt, pilvimääritykset ja verkkoarkkitehtuurin.",
    techDocS3I1: "Hosting- ja pilvinfrastruktuurin dokumentaatio (AWS, Azure, GCP)",
    techDocS3I2: "Infrastructure-as-Code -skriptit (Terraform, CloudFormation, Pulumi)",
    techDocS3I3: "Verkkotopologia ja arkkitehtuurikaaviot",
    techDocS3I4: "Skaalautuvuusarkkitehtuuri ja kuormituksentasauskonfiguraatio",
    techDocS4Title: "CI/CD & DevOps",
    techDocS4Desc: "Prosessit ja työkalut, joita käytetään ohjelmistojen luotettavaan rakentamiseen, testaamiseen ja käyttöönottoon.",
    techDocS4I1: "CI/CD-putkiston dokumentaatio ja työkalut (Jenkins, GitHub Actions jne.)",
    techDocS4I2: "Käyttöönottomenettelyt ja peruutussuunnitelmat",
    techDocS4I3: "Ympäristömäärittely (kehitys, staging, tuotanto)",
    techDocS4I4: "Julkaisutahti, versiointi- ja muutoslokihistoria",
    techDocS5Title: "Tietokanta & Tiedonhallinta",
    techDocS5Desc: "Dokumentaatio, joka osoittaa, että tiedot ovat hyvin jäsenneltyjä, varmuuskopioituja ja palautettavissa.",
    techDocS5I1: "Tietokantaskeema ja entiteetti-suhdekaaviot (ER)",
    techDocS5I2: "Tietojen säilytys- ja arkistointikäytännöt",
    techDocS5I3: "Varmuuskopiointi- ja palautusmenettelyt testituloksineen",
    techDocS5I4: "Tietojen siirtohistoria ja työkaludokumentaatio",
    techDocS6Title: "Tietoturva & Käyttöoikeuksien hallinta",
    techDocS6Desc: "Käytännöt ja hallintamekanismit, jotka suojaavat järjestelmää luvattomalta käytöltä ja hyökkäyksiltä.",
    techDocS6I1: "Identiteetin ja käyttöoikeuksien hallinnan (IAM) dokumentaatio",
    techDocS6I2: "Salaisuuksien hallintamenetelmä (holvit, avainten kierrätyskäytännöt)",
    techDocS6I3: "Verkon tietoturvadokumentaatio (palomuurit, VPN:t, segmentointi)",
    techDocS6I4: "Tietoturvakäytäntöjen ja -menettelyjen dokumentaatio",
    techDocS7Title: "Vaatimustenmukaisuus & Sertifioinnit",
    techDocS7Desc: "Viralliset sertifioinnit ja tarkastusraportit, jotka osoittavat sääntelyvaatimusten ja tietoturvan noudattamisen.",
    techDocS7I1: "Tietoturvasertifioinnit (ISO 27001, SOC 2 Type II, PCI-DSS)",
    techDocS7I2: "Tietosuojan noudattamisen dokumentaatio (GDPR, CCPA, PIPEDA)",
    techDocS7I3: "Tunkeutumistestiraportit viimeiseltä 12–24 kuukaudelta",
    techDocS7I4: "Haavoittuvuudenhallintahistoria ja korjauskäytäntö",
    techDocS8Title: "API & Kolmannen osapuolen integraatiot",
    techDocS8Desc: "Dokumentaatio siitä, miten järjestelmä paljastaa tietoja ja yhdistyy ulkoisiin palveluihin.",
    techDocS8I1: "API-määrittelyt ja -dokumentaatio (OpenAPI/Swagger)",
    techDocS8I2: "Kolmannen osapuolen integraatioiden luettelo toimittajan SLA:ineen",
    techDocS8I3: "Todennus- ja valtuutusdokumentaatio (OAuth, SSO, API-avaimet)",
    techDocS8I4: "Webhook- ja tapahtumalähtöisten integraatioiden dokumentaatio",
    techDocS9Title: "Palautuminen katastrofitilanteesta & Liiketoiminnan jatkuvuus",
    techDocS9Desc: "Suunnitelmat ja näyttö siitä, että järjestelmä voi toipua vikaantumisista ja jatkaa toimintaansa.",
    techDocS9I1: "Katastrofista palautumisen (DR) suunnitelma ja liiketoiminnan jatkuvuussuunnitelma (BCP)",
    techDocS9I2: "RTO/RPO-tavoitteet ja historialliset testitulokset",
    techDocS9I3: "Poikkeamien hallintamenettelyt ja eskalointidokumentaatio",
    techDocS9I4: "Poikkeamaraportit viimeiseltä 24 kuukaudelta",
    techDocS10Title: "Tekninen velka & Tiekartta",
    techDocS10Desc: "Avoimuus tunnetuista rajoituksista, tulevaisuuden suunnitelmista ja avoimen lähdekoodin velvoitteista.",
    techDocS10I1: "Tunnetun teknisen velan luettelo ja priorisointi",
    techDocS10I2: "Käyttöikänsä päähän tulleiden tai poistettujen komponenttien ja kirjastojen lista",
    techDocS10I3: "Tuote- ja tekninen tiekartta (12–24 kuukautta)",
    techDocS10I4: "Avoimen lähdekoodin lisenssivelvoitteet ja niiden noudattaminen",
    techDocS11Title: "Tilinpäätös & Tilintarkastus",
    techDocS11Desc: "Tilintarkastetut tilinpäätökset ja riippumattomat tarkastusraportit, jotka kattavat kolme viisi viimeistä tilikautta.",
    techDocS11I1: "Tilintarkastetut vuositilinpäätökset (tuloslaskelma, tase, kassavirtalaskelma) viimeiseltä 3–5 vuodelta",
    techDocS11I2: "Tilintarkastuskertomukset ja johdon kirjeet riippumattomalta tilintarkastajalta",
    techDocS11I3: "Välitilitys ja johdon raportit (kuluvalta tilikaudelta)",
    techDocS11I4: "Tilinpäätösperiaatteet sekä mahdolliset aiempien vuosien oikaisut tai uudelleenlaadinnat",
    techDocS12Title: "Liikevaihto & Liiketoimintamalli",
    techDocS12Desc: "Yksityiskohtainen erittely tulovirtauksista, asiakaskonsentraatiosta ja liiketoimintamallin kestävyydestä.",
    techDocS12I1: "Liikevaihdon jakauma tuotteen, palvelulinjan, maantieteen ja asiakassegmentin mukaan",
    techDocS12I2: "10 suurimman asiakkaan lista sopimustietoineen, uusimisajankohtineen ja churn-tietoineen",
    techDocS12I3: "Toistuvan ja kerta-liikevaihdon analyysi sekä kohorttikohtainen pidätysdata",
    techDocS12I4: "Hintamallin dokumentaatio ja historialliset hinnoittelumuutokset",
    techDocS13Title: "Käyttöpääoma & Kassavirta",
    techDocS13Desc: "Analyysi yrityksen maksuvalmiudesta, kassavirran tuotosta ja olemassa olevista velkasitoumuksista.",
    techDocS13I1: "Käyttöpääomaanalyysi (myyntisaamisten/ostovelkojen erääntyminen, varastotasot, päivät avoimena)",
    techDocS13I2: "Historialliset ja ennustetut kassavirtalaskelmat keskeisine oletuksineen",
    techDocS13I3: "Velka-aikataulu, luottofasiliteetit, covenantit ja mahdolliset pantiksi asetetut omaisuuserät",
    techDocS13I4: "Investointimenot (CapEx) historiallisesti sekä jakauma ylläpito- ja kasvukapexiin",
    techDocS14Title: "Vero- & Juridiset Velvoitteet",
    techDocS14Desc: "Yleiskatsaus veronmaksun noudattamiseen, avoimiin velvoitteisiin ja olennaisiin juridisiin tai sääntelyaltistuksiin.",
    techDocS14I1: "Jätetyt veroilmoitukset viimeiseltä 3–5 vuodelta (yhteisövero, arvonlisävero)",
    techDocS14I2: "Avoimet veroriidat, veropäätökset tai käynnissä olevat verovalvontakyselyt",
    techDocS14I3: "Aikataulu ehdollisista velvoitteista, varauksista ja taseen ulkopuolisista eräistä",
    techDocS14I4: "Olennaiset sopimukset, vireillä olevat oikeudenkäynnit ja viranomaiskirjeenvaihto",
  },
};

export const useTranslation = (language: LanguageCode) => {
  const t = (key: keyof TranslationKeys): string => {
    return translations[language][key] || translations.en[key] || key;
  };
  
  return { t };
};

export default translations;
